import { api, fmtDuration } from "../api";
import { add, confirmDialog, h, modal, toast } from "../dom";
import type { AdminFlag, AssignmentPayload, FlagReply, FlagShape, GraphData, GraphFlag, LabelClass, TileImage, User } from "../types";
import { Graph, Txn } from "./graph";
import { nearestEdge, nearestNode, pointInPolygon } from "./snap";
import { deleteAt, extendDangling, insertEdge, mergeNodes, resolveAnchor, resolveCrossings } from "./tools";
import { Viewport } from "./view";
import { showHelp } from "./help";
import { simplifyGraph } from "./simplify";
import { CogLayer } from "./cog";

type Mode = "draw" | "paint" | "move" | "soft" | "join" | "extend" | "delete" | "reclass" | "flag" | "measure";

interface Settings {
  snap_dist: number;
  node_spacing: number;
  line_width: number;
  node_size: number;
  snap_intersections: boolean;
  auto_scroll: boolean;
  soft_radius: number;   // brush radius in screen px
  soft_falloff: number;  // falloff exponent: low = broad, high = peaky
  simplify_tolerance: number; // Douglas-Peucker tolerance in image px
  extend_dist: number; // auto-connect max extension in image px (batch action)
}

const ALL_MODES: Mode[] = ["draw", "paint", "move", "soft", "join", "extend", "delete", "reclass", "flag", "measure"];
const DEFAULT_MODE_KEYS: Record<Mode, string> = { draw: "a", paint: "b", move: "m", soft: "s", join: "j", extend: "e", delete: "d", reclass: "r", flag: "n", measure: "t" };
// single-letter keys with a fixed meaning that tool shortcuts must not shadow
const RESERVED_KEYS = new Set(["h", "g", "v", "f"]);
const MODE_NAMES: Record<Mode, string> = {
  draw: "Click", paint: "Paint", move: "Move", soft: "Soft move", join: "Join",
  extend: "Extend & snap", delete: "Delete", reclass: "Reassign class", flag: "Flag", measure: "Measure",
};
const MODE_DESCS: Record<Mode, string> = {
  draw: "", paint: "",
  move: "drag node; lasso-select, then drag together; double-click a line to insert a vertex",
  soft: "push nodes with a falloff brush; double-click a line to insert a vertex",
  join: "merge all nodes in the circle into one at the cursor",
  extend: "sweep along a road; line ends of the current class inside the circle get extended and connected to it",
  delete: "click node or line; Shift+click dissolves a vertex without cutting the line",
  reclass: "click edge, double-click whole line; drag = stencil, repaints only the longest track through it",
  flag: "click = flag a spot; drag = select label segments, then Enter = note on them; Shift+click… = draw an area (close at start) or line (right-click ends); click a flag to read, reply, resolve",
  measure: "click to chain a measuring tape; right-click ends it, Esc clears it",
};
const MODE_ICONS: Record<Mode, string> = { draw: "＋", paint: "✎", move: "✥", soft: "∿", join: "⊕", extend: "⤳", delete: "✕", reclass: "⇄", flag: "⚑", measure: "↔" };

export async function renderEditor(root: HTMLElement, assignmentId: number, user: User, viewTile = false): Promise<() => void> {
  // viewTile: assignmentId is a tile id; read-only preview without claiming
  const payload = await api<AssignmentPayload>(viewTile ? `/api/tiles/${assignmentId}/view` : `/api/assignments/${assignmentId}`);
  const project = payload.project;
  const isOwner = payload.assignment.owner?.id === user.id;
  // admins may edit finished tiles in place ("retouche"): untimed — no heartbeats
  // are sent, so neither the owner nor the admin gains labeling time. The server
  // snapshots the pre-retouche graph on the first retouche save.
  // (also tiles under review: the owner is locked out then, so admins can fix small things and accept)
  const inReview = payload.assignment.status === "review";
  const retouche = (payload.assignment.status === "done" || inReview) && user.is_admin && !viewTile;
  const review = payload.assignment.review ?? { rounds: 0, note: "", cells: [], submitted_at: null, reviewed_at: null, reviewed_by: null };
  // owner working on a tile that came back from review
  const returned = payload.assignment.status === "active" && review.rounds > 0;
  const readOnly = (payload.assignment.status !== "active" || !isOwner) && !retouche;

  // ---------- persistent per-project settings ----------
  const settingsKey = `labeling.settings.p${project.id}`;
  const settings: Settings = {
    snap_dist: project.settings.snap_dist,
    node_spacing: project.settings.node_spacing,
    line_width: project.settings.line_width,
    node_size: 4,
    snap_intersections: project.settings.snap_intersections,
    auto_scroll: true,
    soft_radius: 80,
    soft_falloff: 1.5,
    simplify_tolerance: 1.5,
    extend_dist: 40,
    ...JSON.parse(localStorage.getItem(settingsKey) || "{}"),
  };
  const saveSettings = () => localStorage.setItem(settingsKey, JSON.stringify(settings));

  // ---------- graph + crash backup ----------
  const graph = new Graph();
  graph.load(payload.assignment.graph);
  const backupKey = `labeling.wip.a${assignmentId}`;
  if (!readOnly) {
    const raw = localStorage.getItem(backupKey);
    if (raw) {
      try {
        const backup = JSON.parse(raw) as { t: number; graph: GraphData };
        const serverNodes = payload.assignment.graph.nodes.length;
        if (backup.graph.nodes.length !== serverNodes || backup.graph.edges.length !== payload.assignment.graph.edges.length
            || (backup.graph.flags?.length ?? 0) !== (payload.assignment.graph.flags?.length ?? 0)) {
          if (await confirmDialog("A locally saved (unsynced) version of this tile was found — restore it?", "Restore")) {
            graph.load(backup.graph);
            graph.rev++; // mark dirty so it gets saved to the server
          }
        }
      } catch { /* ignore corrupt backup */ }
    }
  }
  let savedRev = graph.rev;
  let saving = false;
  let saveFailed = false;

  // ---------- classes ----------
  // the class bar is a user-chosen, ordered subset of the active classes
  // (keys 1-9); edited under "Class buttons" in the menu — no auto-shuffling
  const activeClasses = project.classes.filter((c) => c.is_active);
  const classById = new Map(activeClasses.map((c) => [c.id, c]));
  const barKey = `labeling.classbar.p${project.id}`;
  const recentKey = `labeling.recent.p${project.id}`; // pre-classbar LRU slots, migrated below
  let barClasses: number[] = (JSON.parse(localStorage.getItem(barKey)
    || localStorage.getItem(recentKey) || "[]") as number[]).filter((id) => classById.has(id)).slice(0, 9);
  localStorage.removeItem(recentKey);
  if (barClasses.length === 0) barClasses = activeClasses.slice(0, 5).map((c) => c.id);
  const saveBar = () => localStorage.setItem(barKey, JSON.stringify(barClasses));
  let currentClass = barClasses[0] ?? activeClasses[0]?.id ?? 0;

  function selectClass(id: number): void {
    if (!classById.has(id)) return;
    currentClass = id;
    updateHud();
    requestRender();
  }

  // ---------- keyboard shortcuts (tool keys are rebindable) ----------
  const keysKey = "labeling.keys";
  const modeKeys: Record<Mode, string> = { ...DEFAULT_MODE_KEYS };
  try {
    const stored = JSON.parse(localStorage.getItem(keysKey) || "{}") as Partial<Record<Mode, string>>;
    const candidate = { ...DEFAULT_MODE_KEYS };
    for (const m of ALL_MODES) {
      const k = stored[m];
      if (typeof k === "string" && /^[a-z]$/.test(k) && !RESERVED_KEYS.has(k)) candidate[m] = k;
    }
    // a corrupt/conflicting store falls back to the defaults wholesale
    if (new Set(Object.values(candidate)).size === ALL_MODES.length) Object.assign(modeKeys, candidate);
  } catch { /* defaults */ }
  const keyOf = (m: Mode) => modeKeys[m].toUpperCase();
  const helpKeys = () => Object.fromEntries(ALL_MODES.map((m) => [m, keyOf(m)]));
  const modeLabel = (m: Mode) => `${MODE_NAMES[m]} (${keyOf(m)})${MODE_DESCS[m] ? ": " + MODE_DESCS[m] : ""}`;
  const saveKeys = () => localStorage.setItem(keysKey, JSON.stringify(modeKeys));

  // ---------- modality images ----------
  // COGs (.tif) stream tile-by-tile via CogLayer; legacy JPEGs load whole.
  // Elevation (DTM) modalities never show as 2D layers — they feed the 3D
  // view; fall back to everything if a project consists of nothing else.
  const layerImages = payload.images.some((im) => !im.is_elevation)
    ? payload.images.filter((im) => !im.is_elevation)
    : payload.images;
  const images: { info: TileImage; img: HTMLImageElement | null; cog: CogLayer | null; loaded: boolean }[] =
    layerImages.map((info) => {
      if (info.url.endsWith(".tif")) {
        const entry = { info, img: null, cog: null as CogLayer | null, loaded: false };
        entry.cog = new CogLayer(info.url, () => {
          if (entry.cog?.failed) {
            toast(`Image for modality "${info.modality_name}" failed to load despite retries — please reload the page. If it keeps failing, the raster may have been deleted; re-ingest it.`, true);
            return;
          }
          entry.loaded = true;
          requestRender();
        });
        return entry;
      }
      const img = new Image();
      img.src = info.url;
      const entry = { info, img, cog: null, loaded: false };
      img.onload = () => { entry.loaded = true; requestRender(); };
      img.onerror = () => toast(`Image for modality "${info.modality_name}" failed to load — was it deleted? Re-ingest the rasters.`, true);
      return entry;
    });
  let imageIdx = Math.max(0, images.findIndex((i) => i.info.is_reference));

  // ---------- DOM ----------
  const canvas = h("canvas", { className: "editor-canvas" });
  const hud = h("div", { className: "hud" });
  root.append(canvas, hud);
  const viewport = new Viewport(canvas);
  const ctx = canvas.getContext("2d")!;

  // ---------- editor state ----------
  let mode: Mode = "draw";
  let activeNode: number | null = null; // chain-drawing anchor
  let cursor: { x: number; y: number; sx: number; sy: number } | null = null;
  let spaceHeld = false;
  let seconds = payload.assignment.seconds;
  let lastActivity = Date.now();
  const inactivityMs = Math.max(10, project.settings.inactivity_timeout ?? 180) * 1000;
  let menuOpen = false;

  const pointers = new Map<number, { sx: number; sy: number }>();
  let pinch: { dist: number; mx: number; my: number } | null = null;
  let panning: { sx: number; sy: number } | null = null;
  let clickCandidate: { sx: number; sy: number; button: number; mod: boolean } | null = null;
  let stroke: { txn: Txn; last: number } | null = null;
  let grabbed: { id: number; ox: number; oy: number } | null = null;
  let erasing: Txn | null = null;
  let rightMoved = false;
  let lastReclass: { t: number; edgeId: number; fromClass: number; committed: boolean } | null = null;
  let lasso: { x: number; y: number }[] | null = null; // image coords; delete/move drag
  let reclassSweep: { touched: Set<number> } | null = null; // reassign stencil sweep (edge ids)
  const selectedNodes = new Set<number>(); // move-mode lasso selection
  let softDrag: { originals: Map<number, { x: number; y: number }>; lastX: number; lastY: number; sx0: number; sy0: number } | null = null;
  let groupDrag: { originals: Map<number, { x: number; y: number }>; lastX: number; lastY: number } | null = null;
  let extending: { txn: Txn; count: number; tried: Set<number> } | null = null; // extend-tool sweep
  let measurePts: { x: number; y: number }[] = []; // measuring-tape vertices (ephemeral, never saved)
  let measureDone = false; // right-click froze the tape; next click starts a new one
  let flagChain: { x: number; y: number }[] | null = null; // flag tool: Shift+click shape in progress
  const flagSel = new Set<number>(); // flag tool: lasso-selected edge ids (a "labels" flag in the making)
  let lassoAdditive = false; // Shift held when a flag lasso started: add to the selection
  // diff to a baseline (docs/diff.md): admins only, needs pre-labels or a pre-retouche snapshot
  type DiffCat = "kept" | "moved" | "new" | "reclassed";
  type DiffResult = { categories: Record<string, DiffCat>; deleted: number[][]; ties: number[][]; summary: any };
  const canDiff = user.is_admin && !viewTile && (payload.tile.has_seed || payload.tile.has_original);
  let diffOn = false;
  let diffBaseline: "seed" | "original" = payload.tile.has_seed ? "seed" : "original";
  let diffTol = 8;
  let diffResult: DiffResult | null = null;
  let diffBusy = false;
  let diffTimer: number | undefined;
  const DIFF_COLORS: Record<DiffCat, string> = { kept: "", moved: "#ffb400", new: "#3ddc84", reclassed: "#c77dff" };
  let lastVertexClick: { t: number; edgeId: number } | null = null; // double-click-to-insert-vertex tracking
  let showLabels = true; // V toggles label visibility to inspect the imagery below
  let autoPanActive = false; // edge auto-scroll loop while paint/move/lasso dragging
  let disposed = false;

  // ---------- progress grid ----------
  // Server-persisted per assignment; the localStorage copy only holds changes
  // that have not reached the server yet (or a pre-server-persistence legacy
  // grid), so when present it is newer than the server state and wins.
  // keyed by review round: the server clears the grid on a return, and that
  // reset must win over a local copy cached during the previous round
  const gridKey = `labeling.grid.a${assignmentId}.r${review.rounds}`;
  localStorage.removeItem(`labeling.grid.a${assignmentId}`); // pre-review-era key
  if (review.rounds) localStorage.removeItem(`labeling.grid.a${assignmentId}.r${review.rounds - 1}`);
  type GridStored = { nx?: number; ny?: number; checked?: number[] };
  let gridLocal: GridStored | null = null;
  try { gridLocal = JSON.parse(localStorage.getItem(gridKey) || "null"); } catch { /* corrupt: ignore */ }
  const gridStored: GridStored = gridLocal ?? payload.assignment.grid ?? {};
  const gridState = { nx: gridStored.nx ?? 5, ny: gridStored.ny ?? 5 };
  const gridChecked = new Set<number>(gridStored.checked ?? []);
  let gridOn = false;
  let gridEdit = false; // clicking toggles cells; ends when a tool is selected
  let gridDirty = false; // changed since the last successful server push
  let gridSyncTimer: number | undefined;

  function pushGrid(): void {
    if (readOnly) return;
    window.clearTimeout(gridSyncTimer);
    gridDirty = false;
    api(`/api/assignments/${assignmentId}/grid`, {
      method: "PUT", body: { nx: gridState.nx, ny: gridState.ny, checked: [...gridChecked] },
    }).then(() => {
      if (!gridDirty) localStorage.removeItem(gridKey);
    }).catch(() => {
      gridDirty = true; // kept in localStorage; retried on the next change or reload
    });
  }

  function saveGrid(): void {
    localStorage.setItem(gridKey, JSON.stringify({ nx: gridState.nx, ny: gridState.ny, checked: [...gridChecked] }));
    gridDirty = true;
    window.clearTimeout(gridSyncTimer);
    gridSyncTimer = window.setTimeout(pushGrid, 1_000);
  }

  if (gridLocal && !readOnly) pushGrid(); // sync a leftover local grid to the server

  function toggleGrid(): void {
    gridOn = !gridOn;
    gridEdit = gridOn;
    if (gridOn) toast("Grid on — click cells to mark them done; pick a tool to go back to labeling");
    updateHud();
    requestRender();
  }

  function setGridSize(nx: number, ny: number): void {
    nx = Math.max(1, Math.min(50, Math.round(nx) || 5));
    ny = Math.max(1, Math.min(50, Math.round(ny) || 5));
    if (nx === gridState.nx && ny === gridState.ny) return;
    gridState.nx = nx;
    gridState.ny = ny;
    gridChecked.clear(); // grid changed: progress is meaningless now
    saveGrid();
    requestRender();
  }

  function snapDistImage(): number {
    return settings.snap_dist / viewport.scale;
  }

  // ---------- measuring ----------
  const pxSize = payload.ref.px_size;

  /** Distance in image px, shown in meters when the reference pixel size is known. */
  function fmtDist(px: number): string {
    if (pxSize == null) return px >= 100 ? `${Math.round(px)} px` : `${px.toFixed(1)} px`;
    const m = px * pxSize;
    if (m >= 1000) return `${(m / 1000).toFixed(2)} km`;
    return m >= 100 ? `${Math.round(m)} m` : `${m.toFixed(1)} m`;
  }

  // ---------- rendering ----------
  let renderQueued = false;
  function requestRender(): void {
    if (renderQueued) return;
    renderQueued = true;
    requestAnimationFrame(() => {
      renderQueued = false;
      render();
    });
  }

  function render(): void {
    const dpr = viewport.dpr;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#181a1b";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const entry = images[imageIdx];
    if (entry && entry.loaded) {
      const A = entry.info.to_ref;
      const s = viewport.scale;
      ctx.setTransform(
        dpr * s * A[0], dpr * s * A[3],
        dpr * s * A[1], dpr * s * A[4],
        dpr * (s * A[2] + viewport.tx), dpr * (s * A[5] + viewport.ty),
      );
      ctx.imageSmoothingEnabled = s < 4;
      if (entry.cog) entry.cog.draw(ctx, canvas.width, canvas.height);
      else if (entry.img) ctx.drawImage(entry.img, 0, 0);
    }

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    // progress grid (under the label vectors)
    if (gridOn) {
      const gw = payload.ref.width;
      const gh = payload.ref.height;
      const cw = gw / gridState.nx;
      const ch = gh / gridState.ny;
      ctx.fillStyle = "rgba(64, 200, 96, 0.25)";
      for (const idx of gridChecked) {
        const cyi = Math.floor(idx / gridState.nx);
        const cxi = idx % gridState.nx;
        if (cxi >= gridState.nx || cyi >= gridState.ny) continue;
        const [sx, sy] = viewport.toScreen(cxi * cw, cyi * ch);
        ctx.fillRect(sx, sy, cw * viewport.scale, ch * viewport.scale);
      }
      if (returned && review.cells.length) {
        // cells holding the reviewer's flags: "needs edits in at least N cells"
        ctx.strokeStyle = "#ffb400";
        ctx.lineWidth = 4;
        for (const idx of review.cells) {
          const cyi = Math.floor(idx / gridState.nx);
          const cxi = idx % gridState.nx;
          if (cxi >= gridState.nx || cyi >= gridState.ny) continue;
          const [sx, sy] = viewport.toScreen(cxi * cw, cyi * ch);
          ctx.strokeRect(sx + 3, sy + 3, cw * viewport.scale - 6, ch * viewport.scale - 6);
        }
      }
      ctx.strokeStyle = "#8fd0f5";
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (let i = 0; i <= gridState.nx; i++) {
        const [sx, sy0] = viewport.toScreen(i * cw, 0);
        const [, sy1] = viewport.toScreen(i * cw, gh);
        ctx.moveTo(sx, sy0);
        ctx.lineTo(sx, sy1);
      }
      for (let j = 0; j <= gridState.ny; j++) {
        const [sx0, sy] = viewport.toScreen(0, j * ch);
        const [sx1] = viewport.toScreen(gw, j * ch);
        ctx.moveTo(sx0, sy);
        ctx.lineTo(sx1, sy);
      }
      ctx.stroke();
    }

    // edges, batched per class
    if (!showLabels) {
      // labels hidden (V): still show lasso/brush/measure indicators below
      if (lasso && lasso.length > 1) drawLasso();
      drawMeasure();
      drawBrushCircle();
      return;
    }
    const byClass = new Map<number, Path2D>();
    // diff overlay on: unchanged edges lose their class color (white), so the
    // category colors are the only colors on screen
    const diffKept = diffOn && diffResult ? diffResult.categories : null;
    const KEPT_KEY = -1;
    for (const e of graph.edges.values()) {
      const a = graph.nodes.get(e.a)!;
      const b = graph.nodes.get(e.b)!;
      const key = diffKept && (diffKept[String(e.i)] ?? "kept") === "kept" ? KEPT_KEY : e.c;
      let path = byClass.get(key);
      if (!path) { path = new Path2D(); byClass.set(key, path); }
      const [ax, ay] = viewport.toScreen(a.x, a.y);
      const [bx, by] = viewport.toScreen(b.x, b.y);
      path.moveTo(ax, ay);
      path.lineTo(bx, by);
    }
    ctx.lineCap = "round";
    ctx.lineWidth = settings.line_width;
    for (const [cid, path] of byClass) {
      ctx.strokeStyle = cid === KEPT_KEY ? "#e8e6e3" : classById.get(cid)?.color ?? "#ffffff";
      ctx.stroke(path);
    }
    if (diffOn && diffResult) drawDiff();

    // preview segment while chaining / painting
    if (!readOnly && cursor && (mode === "draw" || mode === "paint")) {
      const anchor = mode === "draw" ? activeNode : stroke?.last ?? null;
      if (anchor !== null && graph.nodes.has(anchor)) {
        const a = graph.nodes.get(anchor)!;
        const [ax, ay] = viewport.toScreen(a.x, a.y);
        ctx.strokeStyle = classById.get(currentClass)?.color ?? "#fff";
        ctx.globalAlpha = 0.6;
        ctx.lineWidth = settings.line_width;
        ctx.beginPath();
        ctx.moveTo(ax, ay);
        ctx.lineTo(cursor.sx, cursor.sy);
        ctx.stroke();
        ctx.globalAlpha = 1;
      }
    }

    // lasso selection outline
    if (lasso && lasso.length > 1) drawLasso();
    drawFlagSelection();

    // reassign-stencil sweep: highlight touched edges in the target class color
    if (reclassSweep && reclassSweep.touched.size) {
      ctx.strokeStyle = classById.get(currentClass)?.color ?? "#fff";
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = settings.line_width + 5;
      ctx.lineCap = "round";
      ctx.beginPath();
      for (const ei of reclassSweep.touched) {
        const e = graph.edges.get(ei);
        if (!e) continue;
        const a = graph.nodes.get(e.a);
        const b = graph.nodes.get(e.b);
        if (!a || !b) continue;
        const [ax, ay] = viewport.toScreen(a.x, a.y);
        const [bx, by] = viewport.toScreen(b.x, b.y);
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // nodes (move-mode selection highlighted)
    const r = settings.node_size;
    if (r > 0) {
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = 1;
      for (const n of graph.nodes.values()) {
        const [sx, sy] = viewport.toScreen(n.x, n.y);
        if (sx < -10 || sy < -10 || sx > canvas.width / dpr + 10 || sy > canvas.height / dpr + 10) continue;
        ctx.fillStyle = selectedNodes.has(n.i) ? "#ffd166" : "#ffffff";
        ctx.beginPath();
        ctx.arc(sx, sy, selectedNodes.has(n.i) ? r + 1 : r, 0, Math.PI * 2);
        ctx.fill();
        ctx.stroke();
      }
    }

    // flagged spots + measuring tape (screen-space overlays)
    drawFlags();
    drawMeasure();

    // soft-move brush circle
    drawBrushCircle();

    // active chain node ring
    if (activeNode !== null && graph.nodes.has(activeNode)) {
      const n = graph.nodes.get(activeNode)!;
      const [sx, sy] = viewport.toScreen(n.x, n.y);
      ctx.strokeStyle = "#ffd166";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(sx, sy, r + 4, 0, Math.PI * 2);
      ctx.stroke();
    }

    // snap indicator
    if (!readOnly && cursor && !panning && !pinch && !lasso && !reclassSweep && (mode === "draw" || mode === "move" || mode === "delete" || mode === "reclass")) {
      const sd = snapDistImage();
      const nid = sd > 0 ? nearestNode(graph, cursor.x, cursor.y, sd) : null;
      if (nid !== null) {
        const n = graph.nodes.get(nid)!;
        const [sx, sy] = viewport.toScreen(n.x, n.y);
        ctx.strokeStyle = "#4cc9f0";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(sx, sy, r + 3, 0, Math.PI * 2);
        ctx.stroke();
      } else if (mode !== "move" && sd > 0) {
        const hit = nearestEdge(graph, cursor.x, cursor.y, sd);
        if (hit) {
          const [sx, sy] = viewport.toScreen(hit.x, hit.y);
          ctx.strokeStyle = "#4cc9f0";
          ctx.lineWidth = 2;
          ctx.beginPath();
          ctx.arc(sx, sy, 4, 0, Math.PI * 2);
          ctx.stroke();
        }
      }
    }
  }

  function drawLasso(): void {
    if (!lasso || lasso.length < 2) return;
    ctx.beginPath();
    const [sx0, sy0] = viewport.toScreen(lasso[0].x, lasso[0].y);
    ctx.moveTo(sx0, sy0);
    for (const q of lasso) {
      const [qx, qy] = viewport.toScreen(q.x, q.y);
      ctx.lineTo(qx, qy);
    }
    ctx.closePath();
    ctx.fillStyle = "rgba(76, 201, 240, 0.12)";
    ctx.fill();
    ctx.strokeStyle = "#4cc9f0";
    ctx.lineWidth = 1.5;
    ctx.setLineDash([6, 4]);
    ctx.stroke();
    ctx.setLineDash([]);
  }

  function drawLabelBox(sx: number, sy: number, text: string): void {
    ctx.font = "bold 12px 'Courier New', Courier, monospace";
    const w = ctx.measureText(text).width;
    ctx.fillStyle = "rgba(20, 22, 23, 0.85)";
    ctx.fillRect(sx - 4, sy - 13, w + 8, 18);
    ctx.fillStyle = "#ffd166";
    ctx.fillText(text, sx, sy);
  }

  function drawMeasure(): void {
    if (mode !== "measure" || measurePts.length === 0) return;
    // vertex chain incl. the live cursor segment (unless the tape was ended)
    const chain = [...measurePts];
    if (!measureDone && cursor && !pinch) chain.push({ x: cursor.x, y: cursor.y });
    const pts = chain.map((q) => viewport.toScreen(q.x, q.y));
    ctx.strokeStyle = "#ffd166";
    ctx.lineWidth = 2;
    ctx.setLineDash([7, 5]);
    ctx.beginPath();
    ctx.moveTo(pts[0][0], pts[0][1]);
    for (const [sx, sy] of pts.slice(1)) ctx.lineTo(sx, sy);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#ffd166";
    for (const [sx, sy] of pts.slice(0, measurePts.length)) {
      ctx.beginPath();
      ctx.arc(sx, sy, 3, 0, Math.PI * 2);
      ctx.fill();
    }
    const nSeg = chain.length - 1;
    let total = 0;
    for (let i = 1; i < chain.length; i++) {
      const segLen = Math.hypot(chain[i].x - chain[i - 1].x, chain[i].y - chain[i - 1].y);
      total += segLen;
      if (nSeg > 1) {
        // per-segment length at the segment midpoint (skip screen-tiny ones)
        const screenLen = Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]);
        if (screenLen > 40) {
          drawLabelBox((pts[i][0] + pts[i - 1][0]) / 2 + 8, (pts[i][1] + pts[i - 1][1]) / 2 - 8, fmtDist(segLen));
        }
      }
    }
    const [tipX, tipY] = pts[pts.length - 1];
    drawLabelBox(tipX + 12, tipY - 12, (nSeg > 1 ? "Σ " : "") + fmtDist(total));
  }

  /** Diff overlay (docs/diff.md): changed current edges highlighted by
      category, deleted baseline segments red dashed, moved edges tied to
      their old position. Kept edges stay as drawn. */
  function drawDiff(): void {
    const r = diffResult!;
    ctx.save();
    ctx.lineCap = "round";
    const paths: Record<DiffCat, Path2D> = { kept: new Path2D(), moved: new Path2D(), new: new Path2D(), reclassed: new Path2D() };
    for (const e of graph.edges.values()) {
      const cat = r.categories[String(e.i)];
      if (!cat || cat === "kept") continue;
      const a = graph.nodes.get(e.a), b = graph.nodes.get(e.b);
      if (!a || !b) continue;
      const [ax, ay] = viewport.toScreen(a.x, a.y);
      const [bx, by] = viewport.toScreen(b.x, b.y);
      paths[cat].moveTo(ax, ay);
      paths[cat].lineTo(bx, by);
    }
    // halo so the highlight reads on top of any class color
    for (const cat of ["moved", "new", "reclassed"] as DiffCat[]) {
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = settings.line_width + 5;
      ctx.stroke(paths[cat]);
      ctx.strokeStyle = DIFF_COLORS[cat];
      ctx.lineWidth = settings.line_width + 2;
      ctx.stroke(paths[cat]);
    }
    if (r.ties.length) {
      ctx.strokeStyle = "#ffb400";
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (const [x, y, sx, sy] of r.ties) {
        const [ax, ay] = viewport.toScreen(x, y);
        const [bx, by] = viewport.toScreen(sx, sy);
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
      }
      ctx.stroke();
      ctx.globalAlpha = 1;
    }
    if (r.deleted.length) {
      ctx.beginPath();
      for (const [x1, y1, x2, y2] of r.deleted) {
        const [ax, ay] = viewport.toScreen(x1, y1);
        const [bx, by] = viewport.toScreen(x2, y2);
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
      }
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = settings.line_width + 4;
      ctx.stroke();
      ctx.strokeStyle = "#ff4d6d";
      ctx.lineWidth = settings.line_width + 1;
      ctx.setLineDash([7, 5]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
    ctx.restore();
  }

  /** Ask the server to categorize the live graph against the baseline. */
  async function computeDiff(): Promise<void> {
    if (!diffOn || diffBusy) return;
    diffBusy = true;
    try {
      diffResult = await api<DiffResult>(`/api/assignments/${assignmentId}/diff`,
        { method: "POST", body: { baseline: diffBaseline, tol: diffTol, graph: graph.serialize() } });
    } catch (e: any) {
      toast(`Diff failed: ${e.message}`, true);
      diffOn = false;
    }
    diffBusy = false;
    updateHud();
    if (menuOpen) buildMenu();
    requestRender();
  }
  function scheduleDiff(): void {
    window.clearTimeout(diffTimer);
    diffTimer = window.setTimeout(() => void computeDiff(), 400);
  }
  function toggleDiff(): void {
    diffOn = !diffOn;
    if (diffOn) {
      toast("Diff: white = unchanged, green = new, orange = moved (thin tie to the old position), purple = reclassed, red dashed = deleted from the baseline");
      void computeDiff();
    }
    updateHud();
    requestRender();
  }
  const fmtPx = (px: number) => px >= 1000 ? `${(px / 1000).toFixed(1)}k` : String(Math.round(px));

  /** Flag tool overlays drawn above the labels: the lasso-selected segments
      (yellow glow) and the Shift+click chain in progress (rubber band). */
  function drawFlagSelection(): void {
    if (mode !== "flag") return;
    if (flagSel.size) {
      ctx.save();
      ctx.lineCap = "round";
      ctx.strokeStyle = "#ffd166";
      ctx.globalAlpha = 0.75;
      ctx.lineWidth = 7;
      ctx.beginPath();
      for (const id of flagSel) {
        const e = graph.edges.get(id);
        if (!e) continue;
        const a = graph.nodes.get(e.a), b = graph.nodes.get(e.b);
        if (!a || !b) continue;
        const [ax, ay] = viewport.toScreen(a.x, a.y);
        const [bx, by] = viewport.toScreen(b.x, b.y);
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
      }
      ctx.stroke();
      ctx.restore();
    }
    if (flagChain && flagChain.length) {
      const pts = flagChain.map((q) => viewport.toScreen(q.x, q.y));
      const closable = chainClosable();
      ctx.save();
      ctx.lineJoin = "round";
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
      if (cursor && !pinch) ctx.lineTo(cursor.sx, cursor.sy);
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = 4.5;
      ctx.stroke();
      ctx.strokeStyle = "#ffd166";
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 6]);
      ctx.stroke();
      ctx.setLineDash([]);
      for (const [x, y] of pts) {
        ctx.fillStyle = "#ffd166";
        ctx.beginPath();
        ctx.arc(x, y, 3.5, 0, Math.PI * 2);
        ctx.fill();
      }
      // first vertex: ring when a click there would close the area
      ctx.strokeStyle = closable ? "#2ee65e" : "#ffd166";
      ctx.lineWidth = closable ? 3 : 1.5;
      ctx.beginPath();
      ctx.arc(pts[0][0], pts[0][1], closable ? 11 : 7, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  /** Is the cursor close enough to the chain's first vertex to close it (≥ 3 points)? */
  function chainClosable(): boolean {
    if (!flagChain || flagChain.length < 3 || !cursor) return false;
    return Math.hypot(cursor.x - flagChain[0].x, cursor.y - flagChain[0].y) <= 12 / viewport.scale;
  }

  /** A flag's shape (docs/flags.md), in the flag's state color, dashed with a
      dark halo so it never reads as a label; "labels" shapes glow over the
      segments they refer to. */
  function drawFlagShape(f: GraphFlag | AdminFlag, color: string, hovered: boolean): void {
    const sh = f.shape;
    if (!sh) return;
    ctx.save();
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    if (sh.kind === "labels") {
      ctx.beginPath();
      for (const [x1, y1, x2, y2] of sh.segs) {
        const [ax, ay] = viewport.toScreen(x1, y1);
        const [bx, by] = viewport.toScreen(x2, y2);
        ctx.moveTo(ax, ay);
        ctx.lineTo(bx, by);
      }
      ctx.globalAlpha = 0.55;
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = hovered ? 13 : 11;
      ctx.stroke();
      ctx.globalAlpha = hovered ? 0.85 : 0.6;
      ctx.strokeStyle = color;
      ctx.lineWidth = hovered ? 9 : 7;
      ctx.stroke();
    } else {
      const pts = sh.pts.map(([px, py]) => viewport.toScreen(px, py));
      ctx.beginPath();
      ctx.moveTo(pts[0][0], pts[0][1]);
      for (const [x, y] of pts.slice(1)) ctx.lineTo(x, y);
      if (sh.kind === "area") {
        ctx.closePath();
        ctx.fillStyle = color;
        ctx.globalAlpha = hovered ? 0.22 : 0.12;
        ctx.fill();
        ctx.globalAlpha = 1;
      }
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = hovered ? 6 : 5;
      ctx.stroke();
      ctx.strokeStyle = color;
      ctx.lineWidth = hovered ? 3.5 : 2.5;
      ctx.setLineDash([9, 6]);
      ctx.stroke();
    }
    ctx.restore();
  }

  /** A resolved note on label segments no longer highlights them: the usual
      outcome is that the segments were deleted or redrawn, and a green glow
      over nothing (or over unrelated lines) reads like a leftover label. The
      marker stays, so the thread remains reachable. Drawn areas/lines keep
      their dashed outline — they cannot be mistaken for labels. */
  function shapeShown(f: GraphFlag | AdminFlag): boolean {
    return !!f.shape && (f.shape.kind !== "labels" || flagState(f.i) !== "resolved");
  }

  function drawFlags(): void {
    if (graph.flags.size === 0 && adminFlags.size === 0) return;
    const w = canvas.width / viewport.dpr;
    const hgt = canvas.height / viewport.dpr;
    const hover = mode === "flag" && cursor && !flagChain ? hitFlag(cursor.x, cursor.y) : null;
    const all = [...graph.flags.values(), ...adminFlags.values()];
    // shapes first, markers on top — so a marker is never buried under a fill
    for (const f of all) {
      if (f.shape && shapeShown(f)) drawFlagShape(f, flagColor(f.i), f.i === hover);
    }
    for (const f of all) {
      const [sx, sy] = viewport.toScreen(f.x, f.y);
      if (sx < -60 || sy < -60 || sx > w + 60 || sy > hgt + 60) continue;
      if (f.i === hover) {
        ctx.strokeStyle = "#ffd166";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(sx, sy, 14, 0, Math.PI * 2);
        ctx.stroke();
      }
      if (waitingForMe(f.i)) {
        // white ring: someone else had the last word, this one waits for you
        ctx.strokeStyle = "#1c1c1c";
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.arc(sx, sy, 9, 0, Math.PI * 2);
        ctx.stroke();
        ctx.strokeStyle = "#ffffff";
        ctx.lineWidth = 2.5;
        ctx.stroke();
      }
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = 5.5;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx, sy - 35);
      ctx.stroke();
      ctx.strokeStyle = "#ffffff";
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      ctx.moveTo(sx, sy);
      ctx.lineTo(sx, sy - 35);
      ctx.stroke();
      // state color: red own open / orange admin open / light blue answered / green resolved
      ctx.fillStyle = flagColor(f.i);
      ctx.strokeStyle = "#1c1c1c";
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(sx, sy - 35);
      ctx.lineTo(sx + 23, sy - 27);
      ctx.lineTo(sx, sy - 19);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(sx, sy, 4.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  function drawBrushCircle(): void {
    if (readOnly || !cursor || pinch) return;
    if (mode !== "soft" && mode !== "join" && mode !== "extend" && mode !== "reclass") return;
    ctx.strokeStyle = "rgba(255, 209, 102, 0.75)";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.arc(cursor.sx, cursor.sy, settings.soft_radius, 0, Math.PI * 2);
    ctx.stroke();
    ctx.strokeStyle = "rgba(255, 209, 102, 0.3)";
    ctx.beginPath();
    ctx.arc(cursor.sx, cursor.sy, settings.soft_radius * 0.5, 0, Math.PI * 2);
    ctx.stroke();
  }

  // ---------- HUD ----------
  const saveChip = h("span", { className: "chip" });
  const timeChip = h("span", { className: "chip" });
  const labelsChip = h("span", { className: "chip chip-warn chip-clickable", title: "Show labels (V)", style: { display: "none" }, onClick: () => toggleLabels() }, "labels hidden (V) · editing locked");
  const modalityChip = h("span", { className: "chip chip-clickable", title: "Cycle modality (Tab)", onClick: () => cycleModality(1) });
  const flagChip = h("span", { className: "chip chip-warn chip-clickable", style: { display: "none" },
    title: "Flags waiting for your reply or resolution — click to open the first one", onClick: () => openWaitingFlag() });
  const classbar = h("div", { className: "classbar" });
  const menuPanel = h("div", { className: "menu-panel hidden" });

  // Keep HUD buttons from stealing keyboard focus (a focused button would
  // swallow the spacebar and re-trigger on Space instead of panning).
  hud.addEventListener("mousedown", (e) => {
    if (!menuPanel.contains(e.target as Node)) e.preventDefault();
  });

  hud.append(
    h("button", { className: "hamburger", title: "Menu", onClick: () => toggleMenu() }, "☰"),
    h(
      "div",
      { className: "statusbar" },
      h("span", { className: "chip chip-name" }, payload.tile.basename),
      images.length > 1 ? modalityChip : null,
      labelsChip,
      timeChip,
      retouche ? h("span", { className: "chip chip-warn", title: inReview ? "Reviewing: you may fix things in place; labeling time is not tracked" : "Editing a finished tile as admin — labeling time is not tracked" },
        inReview ? `review · round ${review.rounds + 1} · untimed edits` : "retouche · untimed") : null,
      returned ? h("span", { className: "chip chip-review chip-clickable", title: "Returned from review — click for the reviewer's note", onClick: () => correctionsDialog() },
        `↩ corrections requested · round ${review.rounds}`) : null,
      readOnly ? h("span", { className: "chip chip-ro" },
        payload.assignment.status === "done" ? "read-only · done"
        : inReview ? `read-only · under review${review.submitted_at ? " since " + new Date(review.submitted_at * 1000).toLocaleDateString() : ""}`
        : payload.assignment.status === "view" ? "read-only · not claimed" : "read-only") : saveChip,
      viewTile && payload.tile.queued && !user.is_paused
        ? h("button", { className: "btn btn-small btn-primary", title: "Claim this tile and start labeling it", onClick: () => void claimTile() }, "Claim & label")
        : null,
    ),
    classbar,
    menuPanel,
  );

  function updateHud(): void {
    // class dots + tools
    classbar.textContent = "";
    if (!readOnly) {
      barClasses.forEach((cid, idx) => {
        const cls = classById.get(cid)!;
        classbar.append(
          h("button", {
            className: "classdot" + (cid === currentClass ? " selected" : ""),
            style: { background: cls.color },
            title: `${cls.name} (${idx + 1})`,
            onClick: () => selectClass(cid),
          }),
        );
      });
      classbar.append(h("div", { className: "sep" }));
      for (const m of ALL_MODES.filter((m) => m !== "measure")) {
        classbar.append(
          h("button", {
            className: "toolbtn" + (mode === m ? " selected" : ""),
            title: modeLabel(m),
            onClick: () => setMode(m),
          }, MODE_ICONS[m]),
        );
      }
      classbar.append(h("div", { className: "sep" }));
      classbar.append(
        h("button", { className: "toolbtn", title: "Undo (Ctrl+Z)", onClick: () => doUndo() }, "↶"),
        h("button", { className: "toolbtn", title: "Redo (Ctrl+Shift+Z)", onClick: () => doRedo() }, "↷"),
      );
    }
    // view controls — no keyboard needed (tablets); also available read-only
    classbar.append(h("div", { className: "sep" }));
    if (!readOnly) {
      classbar.append(h("button", { className: "toolbtn toolbtn-text", title: "End line / cancel selection (Esc)", onClick: () => escapeAction() }, "Esc"));
    }
    if (readOnly) {
      // flags stay readable on finished/viewed tiles
      classbar.append(h("button", {
        className: "toolbtn" + (mode === "flag" ? " selected" : ""),
        title: canAdminFlag ? `Flags (${keyOf("flag")}): read & reply, or click an empty spot to plant an admin flag` : `View flags (${keyOf("flag")})`,
        onClick: () => setMode("flag"),
      }, MODE_ICONS.flag));
    }
    classbar.append(
      h("button", { className: "toolbtn" + (mode === "measure" ? " selected" : ""), title: modeLabel("measure"), onClick: () => setMode("measure") }, MODE_ICONS.measure),
    );
    if (canDiff) {
      classbar.append(h("button", {
        className: "toolbtn toolbtn-text" + (diffOn ? " selected" : ""),
        title: "Diff to the pre-labels / pre-retouche graph: what the labeler changed (menu: baseline, tolerance, counts)",
        onClick: () => toggleDiff(),
      }, "Δ"));
    }
    if (can3d) {
      classbar.append(h("button", {
        className: "toolbtn toolbtn-text" + (view3dOpen ? " selected" : ""),
        title: "3D terrain view (view-only): fly over the tile with the labels draped on the elevation model",
        onClick: () => void open3d(),
      }, "3D"));
    }
    classbar.append(
      h("button", { className: "toolbtn", title: "Fit tile to screen (F)", onClick: () => { viewport.fit(payload.ref.width, payload.ref.height); requestRender(); } }, "⛶"),
      h("button", { className: "toolbtn" + (showLabels ? "" : " selected"), title: "Hide/show labels (V)", onClick: () => toggleLabels() }, "👁"),
      h("button", { className: "toolbtn" + (gridOn ? " selected" : ""), title: "Progress grid (G): click cells to mark them done", onClick: () => toggleGrid() }, "▦"),
    );
    const cls = classById.get(currentClass);
    canvas.dataset.mode = readOnly && mode !== "measure" && mode !== "flag" ? "readonly" : mode;
    modalityChip.textContent = images[imageIdx]?.info.modality_name ?? "";
    updateFlagChip();
    updateSaveChip();
    updateTimeChip();
  }

  function updateSaveChip(): void {
    if (readOnly) return;
    saveChip.textContent = saving ? "saving…" : saveFailed ? "save failed — kept locally" : graph.rev !== savedRev ? "unsaved" : "saved";
    saveChip.className = "chip" + (saveFailed ? " chip-bad" : graph.rev !== savedRev ? " chip-warn" : "");
  }

  function updateTimeChip(): void {
    timeChip.textContent = fmtDuration(seconds);
  }

  function buildMenu(): void {
    menuPanel.textContent = "";
    const slider = (label: string, min: number, max: number, step: number, get: () => number, set: (v: number) => void) => {
      const valSpan = h("span", { className: "muted" }, String(get()));
      const input = h("input", {
        type: "range", min, max, step, value: get(),
        onInput: () => { set(Number(input.value)); valSpan.textContent = input.value; saveSettings(); requestRender(); },
      });
      return h("label", { className: "menu-row" }, h("span", {}, label, " ", valSpan), input);
    };
    add(
      menuPanel,
      h("h3", {}, payload.tile.basename),
      (readOnly || retouche) && payload.assignment.owner ? h("p", { className: "muted" }, `labeled by ${payload.assignment.owner.username}`) : null,
      payload.assignment.retouched_by ? h("p", { className: "muted" }, `retouched by ${payload.assignment.retouched_by.username}`) : null,
      h("div", { className: "menu-actions" },
        h("button", { className: "btn", onClick: () => (location.hash = "#/") }, "Back to tiles"),
        viewTile && payload.tile.queued && !user.is_paused ? h("button", { className: "btn btn-primary", onClick: () => void claimTile() }, "Claim & label") : null,
        !readOnly ? h("button", { className: "btn", onClick: () => void save(true) }, "Save now") : null,
        !readOnly && !retouche && !user.needs_review ? h("button", { className: "btn btn-primary", onClick: () => void markDone() }, "Mark as done") : null,
        !readOnly && !retouche ? h("button", { className: "btn" + (user.needs_review ? " btn-primary" : ""), onClick: () => void submitForReview() },
            user.needs_review ? "Submit for review" : "Request review") : null,
        retouche && inReview ? h("button", { className: "btn btn-primary", onClick: () => reviewDialog("accepted") }, "Accept") : null,
        retouche && inReview ? h("button", { className: "btn btn-danger", onClick: () => reviewDialog("returned") }, "Return for corrections") : null,
        h("button", { className: "btn", onClick: () => { toggleMenu(false); showHelp(helpKeys()); } }, "Help")),
      images.length > 1
        ? h("label", { className: "menu-row" }, h("span", {}, "Modality"),
            h("select", { onChange: (e: Event) => { imageIdx = Number((e.target as HTMLSelectElement).value); updateHud(); requestRender(); } },
              ...images.map((im, idx) => h("option", { value: idx, selected: idx === imageIdx }, im.info.modality_name))))
        : null,
      slider("Snap distance (px, 0 = off)", 0, 40, 1, () => settings.snap_dist, (v) => (settings.snap_dist = v)),
      slider("Paint node spacing (image px)", 5, 200, 5, () => settings.node_spacing, (v) => (settings.node_spacing = v)),
      slider("Line width", 1, 10, 0.5, () => settings.line_width, (v) => (settings.line_width = v)),
      slider("Node size", 0, 12, 1, () => settings.node_size, (v) => (settings.node_size = v)),
      slider("Brush radius: soft move / join / extend / reassign (screen px)", 20, 300, 5, () => settings.soft_radius, (v) => (settings.soft_radius = v)),
      slider("Soft-move falloff (low = broad)", 0.5, 3, 0.25, () => settings.soft_falloff, (v) => (settings.soft_falloff = v)),
      h("label", { className: "menu-row row" },
        h("input", { type: "checkbox", checked: settings.snap_intersections,
          onChange: (e: Event) => { settings.snap_intersections = (e.target as HTMLInputElement).checked; saveSettings(); } }),
        h("span", {}, "Snap at line intersections")),
      h("label", { className: "menu-row row" },
        h("input", { type: "checkbox", checked: settings.auto_scroll,
          onChange: (e: Event) => { settings.auto_scroll = (e.target as HTMLInputElement).checked; saveSettings(); } }),
        h("span", {}, "Auto-scroll at screen edge (paint/move/lasso)")),
      h("label", { className: "menu-row row" },
        h("input", { type: "checkbox", checked: showLabels,
          onChange: () => { toggleLabels(); } }),
        h("span", {}, "Show labels (V)")),
      h("label", { className: "menu-row row" },
        h("input", { type: "checkbox", checked: gridOn,
          onChange: () => { toggleGrid(); } }),
        h("span", {}, "Progress grid (G)")),
      (() => {
        const nxIn = h("input", { type: "number", min: 1, max: 50, value: gridState.nx, style: { width: "4.5em" } });
        const nyIn = h("input", { type: "number", min: 1, max: 50, value: gridState.ny, style: { width: "4.5em" } });
        const apply = () => setGridSize(Number(nxIn.value), Number(nyIn.value));
        nxIn.addEventListener("change", apply);
        nyIn.addEventListener("change", apply);
        return h("label", { className: "menu-row" },
          h("span", {}, "Grid subdivisions x × y (changing clears the checkmarks)"),
          h("div", { className: "row gap" }, nxIn, "×", nyIn));
      })(),
      buildClassSection(),
      !readOnly ? (() => {
        const tolIn = h("input", { type: "number", min: 0.1, max: 50, step: 0.1, value: settings.simplify_tolerance, style: { width: "5em" },
          onChange: () => { settings.simplify_tolerance = Number(tolIn.value) || 1.5; saveSettings(); } });
        return h("div", { className: "menu-row" },
          h("span", {}, "Simplify lines (drop nodes within tolerance, image px)"),
          h("div", { className: "row gap" }, tolIn,
            h("button", { className: "btn btn-small", onClick: () => {
                if (labelsHiddenGuard()) return;
                const before = graph.nodes.size;
                const removed = simplifyGraph(graph, settings.simplify_tolerance);
                if (removed > 0) {
                  toast(`${removed} of ${before} nodes removed (Ctrl+Z to undo)`);
                  activeNode = null;
                  touch();
                } else {
                  toast("Nothing to simplify at this tolerance");
                }
              } }, "Apply")));
      })() : null,
      !readOnly ? (() => {
        const distIn = h("input", { type: "number", min: 1, max: 500, value: settings.extend_dist, style: { width: "5em" },
          onChange: () => { settings.extend_dist = Number(distIn.value) || 40; saveSettings(); } });
        return h("div", { className: "menu-row" },
          h("span", {}, `Auto-connect dangling ends of "${classById.get(currentClass)?.name ?? "?"}" (max extension, image px)`),
          h("div", { className: "row gap" }, distIn,
            h("button", { className: "btn btn-small", onClick: () => {
                if (labelsHiddenGuard()) return;
                const txn = new Txn(graph);
                let count = 0;
                for (const nid of [...graph.nodes.keys()]) {
                  if (graph.degree(nid) !== 1) continue;
                  if (graph.edgesOf(nid)[0].c !== currentClass) continue;
                  if (extendDangling(graph, txn, nid, settings.extend_dist)) count++;
                }
                if (txn.commit()) {
                  toast(`${count} line end${count === 1 ? "" : "s"} connected (Ctrl+Z to undo)`);
                  activeNode = null;
                  touch();
                } else {
                  toast("No dangling ends connected at this distance");
                }
              } }, "Run")));
      })() : null,
      canDiff ? buildDiffSection() : null,
      !readOnly ? buildShortcutSection() : null,
      h("p", { className: "muted keyshelp" },
        `Keys: 1–9 classes · ${keyOf("draw")} click · ${keyOf("paint")} paint · ${keyOf("move")} move · `
        + `${keyOf("soft")} soft move · ${keyOf("join")} join · ${keyOf("extend")} extend&snap · `
        + `${keyOf("delete")} delete (Shift+click = dissolve vertex) · ${keyOf("reclass")} reassign · `
        + `${keyOf("flag")} flag (drag = select segments, Shift+click… = draw shape, Enter = note) · ${keyOf("measure")} measure (right-click ends) · `
        + `drag in ${keyOf("move")}/${keyOf("delete")} = lasso · drag in ${keyOf("reclass")} = stencil · `
        + `double-click line in ${keyOf("move")}/${keyOf("soft")} = insert vertex · V labels on/off · G grid · `
        + "Tab modality · right-drag/Space pan · Ctrl+Z undo · F fit · H help"),
    );
  }

  /** Menu section: which classes sit on the bar (keys 1-9), and in which order. */
  function buildClassSection(): HTMLElement {
    const classRow = (c: LabelClass, ...controls: (HTMLElement | null)[]) =>
      h("div", { className: "row menu-class-row" },
        h("button", { className: "menu-class" + (c.id === currentClass ? " selected" : ""), disabled: readOnly,
          onClick: () => { selectClass(c.id); buildMenu(); } },
          h("span", { className: "classdot-small", style: { background: c.color } }), c.name),
        ...controls);
    if (readOnly) {
      return h("div", { className: "menu-classes" },
        h("h4", {}, "All classes"),
        ...activeClasses.map((c) => classRow(c)));
    }
    const ctl = (label: string, title: string, disabled: boolean, action: () => void) =>
      h("button", { className: "btn btn-small btn-icon", title, disabled,
        onClick: () => { action(); saveBar(); updateHud(); buildMenu(); } }, label);
    const rest = activeClasses.filter((c) => !barClasses.includes(c.id));
    return h("div", { className: "menu-classes" },
      h("h4", {}, "Class buttons (keys 1–9)"),
      ...barClasses.map((cid, idx) => {
        const c = classById.get(cid)!;
        return classRow(c,
          h("span", { className: "muted menu-class-key" }, String(idx + 1)),
          ctl("↑", "Move up", idx === 0, () => { [barClasses[idx - 1], barClasses[idx]] = [barClasses[idx], barClasses[idx - 1]]; }),
          ctl("↓", "Move down", idx === barClasses.length - 1, () => { [barClasses[idx + 1], barClasses[idx]] = [barClasses[idx], barClasses[idx + 1]]; }),
          ctl("✕", "Remove from the bar", barClasses.length <= 1, () => { barClasses.splice(idx, 1); }));
      }),
      rest.length ? h("h4", {}, "More classes") : null,
      ...rest.map((c) => classRow(c,
        barClasses.length < 9
          ? ctl("＋", "Add to the class bar", false, () => { barClasses.push(c.id); })
          : null)));
  }

  /** Menu section: rebindable tool keys. */
  function buildDiffSection(): HTMLElement {
    const sm = diffResult?.summary;
    const box = h("div", { className: "menu-section" }, h("h3", {}, "Diff"));
    const baseSel = h("select", { onChange: () => { diffBaseline = baseSel.value as "seed" | "original"; scheduleDiff(); } },
      payload.tile.has_seed ? h("option", { value: "seed", selected: diffBaseline === "seed" }, "pre-labels (import)") : null,
      payload.tile.has_original ? h("option", { value: "original", selected: diffBaseline === "original" }, "before retouche (as delivered)") : null);
    const tolVal = h("span", { className: "muted" }, `${diffTol} px`);
    const tolIn = h("input", { type: "range", min: 2, max: 40, step: 1, value: diffTol,
      onInput: () => { diffTol = Number(tolIn.value); tolVal.textContent = `${diffTol} px`; scheduleDiff(); } });
    add(box,
      h("label", { className: "row gap" }, h("input", { type: "checkbox", checked: diffOn, onChange: () => toggleDiff() }), " show diff overlay (Δ)"),
      h("label", { className: "menu-row" }, h("span", {}, "Baseline"), baseSel),
      h("label", { className: "menu-row" }, h("span", {}, "Tolerance (image px) ", tolVal), tolIn),
      sm ? h("p", { className: "muted small" },
        `${sm.changed_pct ?? "–"} % of the baseline changed · `
        + `kept ${fmtPx(sm.kept)} · moved ${fmtPx(sm.moved)} · new ${fmtPx(sm.new)} · reclassed ${fmtPx(sm.reclassed)} · deleted ${fmtPx(sm.deleted)} px`
        + ` (baseline ${fmtPx(sm.baseline_px)} px, ${sm.deleted_segments} deleted segments)`)
        : h("p", { className: "muted small" }, diffOn ? "computing…" : "turn the overlay on to compute"));
    return box;
  }

  function buildShortcutSection(): HTMLElement {
    const rows = ALL_MODES.map((m) => {
      const btn = h("button", { className: "btn btn-small key-btn", onClick: () => startRebind(m, btn) }, keyOf(m));
      return h("div", { className: "row menu-row menu-key-row" },
        h("span", {}, `${MODE_ICONS[m]} ${MODE_NAMES[m]}`), btn);
    });
    return h("div", { className: "menu-classes" },
      h("h4", {}, "Tool shortcuts"),
      ...rows,
      h("div", { className: "row right" },
        h("button", { className: "btn btn-small", onClick: () => {
            Object.assign(modeKeys, DEFAULT_MODE_KEYS);
            saveKeys();
            updateHud();
            buildMenu();
          } }, "Reset to defaults")));
  }

  let cancelRebind: (() => void) | null = null;
  function startRebind(m: Mode, btn: HTMLElement): void {
    cancelRebind?.();
    btn.textContent = "press a key…";
    const done = () => {
      window.removeEventListener("keydown", capture, true);
      cancelRebind = null;
      updateHud();
      buildMenu();
    };
    const capture = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.key === "Escape") return done();
      const k = e.key.toLowerCase();
      if (!/^[a-z]$/.test(k) || e.ctrlKey || e.metaKey || e.altKey) {
        toast("Single letters only (A–Z)", true);
        return done();
      }
      if (RESERVED_KEYS.has(k)) {
        toast("That key is taken: H = help, G = grid, V = labels, F = fit", true);
        return done();
      }
      const other = ALL_MODES.find((o) => o !== m && modeKeys[o] === k);
      if (other) {
        toast(`"${k.toUpperCase()}" is already used by ${MODE_NAMES[other]}`, true);
        return done();
      }
      modeKeys[m] = k;
      saveKeys();
      done();
    };
    window.addEventListener("keydown", capture, true);
    cancelRebind = done;
  }

  // ---------- 3D terrain view (loaded on demand; needs an elevation modality) ----------
  const terrainImg = payload.images.find((im) => im.height_url);
  const can3d = !!terrainImg && payload.ref.px_size !== null;
  let view3d: { dispose: () => void } | null = null;
  let view3dOpen = false;

  async function open3d(): Promise<void> {
    if (view3dOpen || !terrainImg || payload.ref.px_size === null) return;
    view3dOpen = true;
    updateHud();
    try {
      const { openView3d } = await import("./view3d");
      if (!view3dOpen) return; // closed while loading
      // 3D drapes from the full payload list: the elevation modality is hidden
      // in 2D but stays available as a texture here (bright = high)
      const textures = payload.images
        .filter((im) => im.url.endsWith(".tif"))
        .map((im) => ({ name: im.modality_name, url: im.url }));
      const current = images[imageIdx];
      const segments = [...graph.edges.values()].map((e) => {
        const a = graph.nodes.get(e.a)!;
        const b = graph.nodes.get(e.b)!;
        return { ax: a.x, ay: a.y, bx: b.x, by: b.y, color: classById.get(e.c)?.color ?? "#999999" };
      });
      const [cx, cy] = viewport.toImage(canvas.clientWidth / 2, canvas.clientHeight / 2);
      view3d = await openView3d({
        root,
        heightUrl: terrainImg.height_url!,
        pxSize: payload.ref.px_size,
        refWidth: payload.ref.width,
        refHeight: payload.ref.height,
        textures,
        startTexture: Math.max(0, textures.findIndex((t) => t.name === current?.info.modality_name)),
        center: { x: cx, y: cy },
        viewWidthPx: canvas.clientWidth / viewport.scale,
        segments,
        onExit: () => {
          view3d = null;
          view3dOpen = false;
          updateHud();
          requestRender();
        },
      });
      if (!view3dOpen) view3d.dispose(); // closed while loading
    } catch (e: any) {
      view3dOpen = false;
      updateHud();
      toast(`3D view failed: ${e.message}`, true);
    }
  }

  function toggleLabels(): void {
    showLabels = !showLabels;
    labelsChip.style.display = showLabels ? "none" : "";
    updateHud();
    requestRender();
  }

  // Editing is locked while the labels are hidden (V) — one might otherwise
  // change invisible features. Measuring, panning and zooming stay available.
  let hiddenToastAt = 0;
  function labelsHiddenGuard(): boolean {
    if (showLabels) return false;
    const now = Date.now();
    if (now - hiddenToastAt > 2500) {
      hiddenToastAt = now;
      toast("Labels are hidden — press V (👁) to show them before editing", true);
    }
    return true;
  }

  /** Escape, also as a toolbar button — touch devices have no Esc key or right-click. */
  function escapeAction(): void {
    if (menuOpen) toggleMenu(false);
    else if (stroke || grabbed || lasso || softDrag || groupDrag || extending || reclassSweep) cancelStroke();
    else if (flagChain) { flagChain = null; requestRender(); }
    else if (flagSel.size) { flagSel.clear(); requestRender(); }
    else if (measurePts.length) { measurePts = []; measureDone = false; requestRender(); }
    else if (selectedNodes.size) { selectedNodes.clear(); requestRender(); }
    else if (activeNode !== null) { activeNode = null; requestRender(); }
  }

  function toggleMenu(force?: boolean): void {
    menuOpen = force ?? !menuOpen;
    if (!menuOpen) cancelRebind?.();
    if (menuOpen) buildMenu();
    else (document.activeElement as HTMLElement | null)?.blur?.();
    menuPanel.classList.toggle("hidden", !menuOpen);
  }

  function setMode(m: Mode): void {
    mode = m;
    activeNode = null;
    lastVertexClick = null;
    if (m !== "measure") { measurePts = []; measureDone = false; }
    if (m !== "flag") { flagChain = null; flagSel.clear(); }
    gridEdit = false; // picking a tool locks the grid checkmarks
    updateHud();
    requestRender();
  }

  function cycleModality(dir: number): void {
    if (images.length < 2) return;
    imageIdx = (imageIdx + dir + images.length) % images.length;
    updateHud();
    requestRender();
  }

  function doUndo(): void { if (labelsHiddenGuard()) return; if (graph.undo()) { activeNode = null; touch(); } }
  function doRedo(): void { if (labelsHiddenGuard()) return; if (graph.redo()) { activeNode = null; touch(); } }

  /** After any graph mutation: refresh chips, backup, rerender. */
  function touch(): void {
    lastActivity = Date.now();
    updateSaveChip();
    requestRender();
  }

  // ---------- out-of-bounds protection ----------
  // The server rejects graphs with geometry beyond the image + 5% margin
  // (same formula as validate_graph), which used to make saving fail until
  // the stray node was found and deleted by hand. Placement outside is
  // refused up front, and anything that still ends up out there (moves,
  // lasso drags, soft-move pushes) is dropped automatically before saving.
  const oobMargin = 0.05 * Math.max(payload.ref.width, payload.ref.height);
  const inImageBounds = (x: number, y: number): boolean =>
    x >= -oobMargin && x <= payload.ref.width + oobMargin && y >= -oobMargin && y <= payload.ref.height + oobMargin;
  let boundsToastAt = 0;
  function outsideToast(): void {
    const now = Date.now();
    if (now - boundsToastAt > 2500) {
      boundsToastAt = now;
      toast("Outside the image — labels must stay on the tile", true);
    }
  }

  /** Drop nodes (with their edges) and flags beyond the margin: one undoable
      step, announced. Skipped while an interaction is in flight. */
  function pruneOutOfBounds(): void {
    if (stroke || grabbed || lasso || softDrag || groupDrag || extending || reclassSweep || erasing) return;
    const badNodes = [...graph.nodes.values()].filter((n) => !inImageBounds(n.x, n.y)).map((n) => n.i);
    const badFlags = [...graph.flags.values()].filter((f) => !inImageBounds(f.x, f.y)).map((f) => f.i);
    if (!badNodes.length && !badFlags.length) return;
    const txn = new Txn(graph);
    const badSet = new Set(badNodes);
    const neighbors = new Set<number>();
    for (const nid of badNodes) {
      for (const e of graph.edgesOf(nid)) {
        const other = e.a === nid ? e.b : e.a;
        if (!badSet.has(other)) neighbors.add(other);
      }
      txn.delNodeWithEdges(nid);
    }
    txn.pruneIsolated([...neighbors]);
    for (const fid of badFlags) txn.delFlag(fid);
    if (txn.commit()) {
      if (activeNode !== null && !graph.nodes.has(activeNode)) activeNode = null;
      const parts = [];
      if (badNodes.length) parts.push(`${badNodes.length} node${badNodes.length === 1 ? "" : "s"}`);
      if (badFlags.length) parts.push(`${badFlags.length} flag${badFlags.length === 1 ? "" : "s"}`);
      toast(`Removed ${parts.join(" and ")} outside the image (Ctrl+Z to undo)`);
      touch();
    }
  }

  // ---------- persistence ----------
  async function save(explicit = false): Promise<boolean> {
    if (diffOn && graph.rev !== savedRev) scheduleDiff();
    if (readOnly || saving) return false;
    pruneOutOfBounds();
    const revAtSave = graph.rev;
    if (revAtSave === savedRev && !explicit) return true;
    saving = true;
    updateSaveChip();
    try {
      await api(`/api/assignments/${assignmentId}/graph`, { method: "PUT", body: { graph: graph.serialize() } });
      savedRev = revAtSave;
      saveFailed = false;
      localStorage.removeItem(backupKey);
      if (explicit) toast("Saved");
      return true;
    } catch (e: any) {
      saveFailed = true;
      localBackup();
      if (explicit) toast(`Save failed: ${e.message}`, true);
      return false;
    } finally {
      saving = false;
      updateSaveChip();
    }
  }

  function localBackup(): void {
    try {
      localStorage.setItem(backupKey, JSON.stringify({ t: Date.now(), graph: graph.serialize() }));
    } catch { /* quota exceeded: nothing we can do */ }
  }

  /** "n unresolved flags …" sentence for the finish/submit confirmations, or "" when clean. */
  function unresolvedFlagsWarning(): string {
    const ids = allFlagIds();
    const unresolved = ids.filter((i) => flagState(i) !== "resolved").length;
    if (!unresolved) return "";
    const waiting = ids.filter(waitingForMe).length;
    const admin = ids.filter((i) => i < 0 && flagState(i) !== "resolved").length;
    return `This tile still has ${unresolved} unresolved flag${unresolved === 1 ? "" : "s"}`
      + (waiting ? ` — ${waiting === unresolved ? (unresolved === 1 ? "it is" : "all of them") : waiting} waiting for your reply or resolution` : "")
      + (admin ? `, ${admin} planted by an admin (orange) — those may ask you to fix something` : "");
  }

  /** Owner hands the tile to the admins (docs/review.md). */
  async function submitForReview(): Promise<void> {
    pruneOutOfBounds();
    const warn = unresolvedFlagsWarning();
    const msg = (warn ? warn + ". " : "")
      + (review.rounds ? `Submit round ${review.rounds + 1} for review? ` : "Submit this tile for review? ")
      + "You cannot edit it while an admin looks at it; it comes back with flags if something needs fixing, or counts as done once accepted."
      + (warn ? " (Cancel and press N to look at the flags first.)" : "");
    if (!(await confirmDialog(msg, warn ? "Submit anyway" : "Submit"))) return;
    try {
      await api(`/api/assignments/${assignmentId}/submit`, { method: "POST", body: { graph: graph.serialize() } });
      localStorage.removeItem(backupKey);
      toast("Submitted for review — thank you!");
      location.hash = "#/";
    } catch (e: any) {
      toast(`Could not submit: ${e.message}`, true);
    }
  }

  /** Admin verdict on a tile under review: accept, or return with a note. */
  function reviewDialog(outcome: "accepted" | "returned"): void {
    const since = review.submitted_at ?? 0;
    const roundFlags = [...adminFlags.values()].filter((f) => f.created_at >= since).length;
    const note = h("textarea", { rows: 4, placeholder: outcome === "returned"
      ? "What needs to change overall? (the flags carry the specifics)" : "Optional note for the labeler" });
    const keep = h("input", { type: "checkbox" });
    const error = h("div", { className: "form-error" });
    const close = modal(
      h("form", { className: "review-dialog", onSubmit: async (e: Event) => {
          e.preventDefault();
          if (outcome === "returned" && !note.value.trim() && !roundFlags) {
            error.textContent = "Say what should change — a note or at least one flag.";
            return;
          }
          try {
            if (graph.rev !== savedRev) await save(true); // admin edits made during the review
            const r = await api<{ cells: number[] }>(`/api/assignments/${assignmentId}/review`,
              { method: "POST", body: { outcome, note: note.value, keep_grid: keep.checked } });
            close();
            toast(outcome === "accepted" ? "Tile accepted and exported" : `Returned for corrections${r.cells.length ? ` — edits needed in at least ${r.cells.length} grid cell${r.cells.length === 1 ? "" : "s"}` : ""}`);
            location.hash = "#/admin/tiles";
          } catch (err: any) {
            error.textContent = err.message;
          }
        } },
        h("h2", {}, outcome === "accepted" ? "Accept this tile?" : "Return for corrections?"),
        h("p", { className: "muted" }, outcome === "accepted"
          ? `Round ${review.rounds + 1}: the tile counts as done for ${payload.assignment.owner?.username ?? "the labeler"} and is exported.`
          : `Round ${review.rounds + 1}: the tile goes back to ${payload.assignment.owner?.username ?? "the labeler"}. `
            + `${roundFlags} flag${roundFlags === 1 ? "" : "s"} planted during this review will be their to-do list (orange, waiting for them).`),
        h("label", {}, "Note", note),
        outcome === "returned"
          ? h("label", { className: "row gap", title: "By default the progress grid is cleared — a return means: walk the tile again. Keep it only for truly local corrections; the cells holding your flags are cleared either way." },
              keep, " keep the progress grid (only the flagged cells are cleared)")
          : null,
        error,
        h("div", { className: "row gap right" },
          h("button", { className: "btn", type: "button", onClick: () => close() }, "Cancel"),
          h("button", { className: "btn " + (outcome === "accepted" ? "btn-primary" : "btn-danger"), type: "submit" },
            outcome === "accepted" ? "Accept" : "Return"))),
    );
    note.focus();
  }

  /** Reviewer's note on a returned tile; shown once per round automatically, then via the chip. */
  function correctionsDialog(): void {
    const waiting = allFlagIds().filter(waitingForMe).length;
    const n = review.cells.length;
    const close = modal(
      h("div", { className: "review-dialog" },
        h("h2", {}, `Corrections requested (round ${review.rounds})`),
        h("p", { className: "muted" }, `${review.reviewed_by ?? "An admin"} · ${review.reviewed_at ? new Date(review.reviewed_at * 1000).toLocaleDateString() : ""}`),
        h("p", { className: "flag-note" }, review.note || h("span", { className: "muted" }, "(no overall note — see the flags)")),
        h("p", {}, (waiting ? `${waiting} flag${waiting === 1 ? "" : "s"} wait for you (press N, then ‹ › to walk through them). ` : "")
          + (n ? `Needs edits in at least ${n} grid cell${n === 1 ? "" : "s"} (outlined orange in the grid, G). ` : "")
          + "The progress grid was reset: please walk the whole tile again, then submit it once more."),
        h("div", { className: "row gap right" },
          waiting ? h("button", { className: "btn", onClick: () => { close(); openWaitingFlag(); } }, "Show first flag") : null,
          h("button", { className: "btn btn-primary", onClick: () => close() }, "OK"))),
    );
  }
  if (returned && !readOnly) {
    const seenKey = `labeling.reviewSeen.a${assignmentId}`;
    if (localStorage.getItem(seenKey) !== String(review.rounds)) {
      localStorage.setItem(seenKey, String(review.rounds));
      setTimeout(correctionsDialog, 400);
    }
  }

  async function markDone(): Promise<void> {
    pruneOutOfBounds();
    const warn = unresolvedFlagsWarning();
    const msg = warn
      ? warn + ". Marking the tile as done finishes it for good; the flags stay on it for the admins. Mark it done anyway? (Cancel and press N to review the flags first.)"
      : "Mark this tile as done? You will not be able to edit it afterwards.";
    if (!(await confirmDialog(msg, warn ? "Mark done anyway" : "Mark done"))) return;
    try {
      await api(`/api/assignments/${assignmentId}/done`, { method: "POST", body: { graph: graph.serialize() } });
      localStorage.removeItem(backupKey);
      toast("Tile finished — thank you!");
      location.hash = "#/";
    } catch (e: any) {
      toast(`Could not finish tile: ${e.message}`, true);
    }
  }

  /** View mode: claim the tile right here instead of going back to the list. */
  async function claimTile(): Promise<void> {
    try {
      const r = await api<{ assignment_id: number }>(`/api/tiles/${payload.tile.id}/claim`, { method: "POST" });
      toast("Tile claimed — it's yours now");
      location.hash = `#/label/${r.assignment_id}`;
    } catch (e: any) {
      toast(`Could not claim: ${e.message}`, true);
    }
  }

  // ---------- input ----------
  /** Extend the current paint stroke toward (x, y), dropping nodes at the configured spacing. */
  function extendStroke(x: number, y: number): void {
    if (!stroke) return;
    if (!inImageBounds(x, y)) return; // the stroke pauses at the margin and resumes inside
    const last = graph.nodes.get(stroke.last);
    if (!last) return;
    if (Math.hypot(x - last.x, y - last.y) >= settings.node_spacing) {
      const nid = stroke.txn.addNode(x, y);
      insertEdge(graph, stroke.txn, stroke.last, nid, currentClass, settings.snap_intersections);
      stroke.last = nid;
    }
  }

  /** Pan direction/speed (px per frame) when the cursor sits near a viewport edge; null when not. */
  function edgePanVector(): [number, number] | null {
    if (!cursor || !settings.auto_scroll) return null;
    const MARGIN = 120;
    const MAX_SPEED = 7;
    const w = canvas.clientWidth;
    const hgt = canvas.clientHeight;
    const smooth = (t: number) => {
      const c = Math.max(0, Math.min(1, t));
      return c * c * (3 - 2 * c); // smoothstep: gentle ramp-in, full speed at the edge
    };
    let dx = 0;
    let dy = 0;
    if (cursor.sx < MARGIN) dx = smooth((MARGIN - cursor.sx) / MARGIN);
    else if (cursor.sx > w - MARGIN) dx = -smooth((cursor.sx - (w - MARGIN)) / MARGIN);
    if (cursor.sy < MARGIN) dy = smooth((MARGIN - cursor.sy) / MARGIN);
    else if (cursor.sy > hgt - MARGIN) dy = -smooth((cursor.sy - (hgt - MARGIN)) / MARGIN);
    if (dx === 0 && dy === 0) return null;
    if (dx !== 0 && dy !== 0) {
      // corner: boost the weaker axis so the motion is clearly diagonal,
      // then normalize so diagonal speed matches straight-edge speed
      const ax = Math.abs(dx);
      const ay = Math.abs(dy);
      const m = Math.max(ax, ay);
      dx = Math.sign(dx) * Math.max(ax, 0.6 * m);
      dy = Math.sign(dy) * Math.max(ay, 0.6 * m);
      const len = Math.hypot(dx, dy);
      dx = (dx / len) * m;
      dy = (dy / len) * m;
    }
    return [dx * MAX_SPEED, dy * MAX_SPEED];
  }

  /** While painting, node-dragging or lassoing near the edge, keep panning and
      feed the (stationary) cursor position back into the interaction. */
  function maintainAutoPan(): void {
    if (autoPanActive) return;
    const step = () => {
      const dragging = stroke !== null || grabbed !== null || lasso !== null || groupDrag !== null || reclassSweep !== null;
      const v = dragging && !disposed ? edgePanVector() : null;
      if (!v || !cursor) {
        autoPanActive = false;
        return;
      }
      viewport.panBy(v[0], v[1]);
      const [x, y] = viewport.toImage(cursor.sx, cursor.sy);
      cursor = { sx: cursor.sx, sy: cursor.sy, x, y };
      lastActivity = Date.now();
      if (stroke) {
        extendStroke(x, y);
      } else if (grabbed) {
        const n = graph.nodes.get(grabbed.id);
        if (n) { n.x = x; n.y = y; }
      } else if (groupDrag) {
        const dx = x - groupDrag.lastX;
        const dy = y - groupDrag.lastY;
        for (const i of groupDrag.originals.keys()) {
          const n = graph.nodes.get(i);
          if (n) { n.x += dx; n.y += dy; }
        }
        groupDrag.lastX = x;
        groupDrag.lastY = y;
      } else if (lasso) {
        const last = lasso[lasso.length - 1];
        if (Math.hypot(x - last.x, y - last.y) * viewport.scale > 3) lasso.push({ x, y });
      } else if (reclassSweep) {
        processReclassSweep(x, y);
      }
      requestRender();
      requestAnimationFrame(step);
    };
    if ((stroke || grabbed || lasso || groupDrag || reclassSweep) && edgePanVector()) {
      autoPanActive = true;
      requestAnimationFrame(step);
    }
  }

  /** Reassign-stencil sweep step: collect edges whose midpoint is inside the
      brush. Midpoint (not segment distance) so that crossing lines which merely
      pass through the brush at a junction are not picked up. */
  function processReclassSweep(x: number, y: number): void {
    if (!reclassSweep) return;
    const radius = settings.soft_radius / viewport.scale;
    for (const e of graph.edges.values()) {
      if (reclassSweep.touched.has(e.i)) continue;
      const a = graph.nodes.get(e.a);
      const b = graph.nodes.get(e.b);
      if (!a || !b) continue;
      if (Math.hypot((a.x + b.x) / 2 - x, (a.y + b.y) / 2 - y) <= radius) reclassSweep.touched.add(e.i);
    }
  }

  /** Longest edge-simple path (by length) through the touched edge set: the
      "track" the sweep followed. Side branches and dangling ends of crossing
      lines only make it in if they would lengthen the path — which a short
      stub at a junction never does while the track continues. */
  function longestTouchedTrack(touched: Set<number>): number[] {
    const adj = new Map<number, { i: number; other: number; len: number }[]>();
    for (const ei of touched) {
      const e = graph.edges.get(ei);
      if (!e) continue;
      const a = graph.nodes.get(e.a);
      const b = graph.nodes.get(e.b);
      if (!a || !b) continue;
      const len = Math.hypot(a.x - b.x, a.y - b.y);
      for (const [from, to] of [[e.a, e.b], [e.b, e.a]]) {
        let list = adj.get(from);
        if (!list) adj.set(from, (list = []));
        list.push({ i: e.i, other: to, len });
      }
    }
    let best: number[] = [];
    let bestLen = -1;
    let steps = 0;
    const used = new Set<number>();
    const path: number[] = [];
    const dfs = (node: number, acc: number): void => {
      if (acc > bestLen) {
        bestLen = acc;
        best = [...path];
      }
      if (++steps > 200_000) return; // sweeps are near-linear; cap pathological cases
      for (const { i, other, len } of adj.get(node) ?? []) {
        if (used.has(i)) continue;
        used.add(i);
        path.push(i);
        dfs(other, acc + len);
        path.pop();
        used.delete(i);
      }
    };
    const nodes = [...adj.keys()];
    const leaves = nodes.filter((n) => (adj.get(n) ?? []).length === 1);
    for (const s of leaves.length ? leaves : nodes) dfs(s, 0);
    return best;
  }

  function finishReclassSweep(): void {
    if (!reclassSweep || reclassSweep.touched.size === 0) return;
    lastActivity = Date.now();
    const track = longestTouchedTrack(reclassSweep.touched);
    const txn = new Txn(graph);
    let count = 0;
    for (const ei of track) {
      const e = graph.edges.get(ei);
      if (e && e.c !== currentClass) {
        txn.setClass(ei, currentClass);
        count++;
      }
    }
    const skipped = reclassSweep.touched.size - track.length;
    if (txn.commit()) {
      toast(`${count} edge${count === 1 ? "" : "s"} reassigned to ${classById.get(currentClass)?.name ?? "?"}`
        + (skipped ? ` · ${skipped} touched side edge${skipped === 1 ? "" : "s"} left alone` : ""));
      touch();
    } else {
      toast("Longest track through the stencil already has this class");
    }
  }

  /** Extend-tool sweep step: connect dangling current-class ends near (x, y). */
  function processExtend(x: number, y: number): void {
    if (!extending) return;
    const radius = settings.soft_radius / viewport.scale;
    for (const nid of [...graph.nodes.keys()]) {
      if (extending.tried.has(nid)) continue;
      const n = graph.nodes.get(nid);
      if (!n || Math.hypot(n.x - x, n.y - y) > radius) continue;
      if (graph.degree(nid) !== 1) continue;
      if (graph.edgesOf(nid)[0].c !== currentClass) continue;
      extending.tried.add(nid);
      if (extendDangling(graph, extending.txn, nid, radius)) {
        extending.count++;
        requestRender();
      }
    }
  }

  function eventPos(e: PointerEvent): { sx: number; sy: number; x: number; y: number } {
    const r = canvas.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const [x, y] = viewport.toImage(sx, sy);
    return { sx, sy, x, y };
  }

  function cancelStroke(): void {
    if (lasso) { lasso = null; requestRender(); }
    if (reclassSweep) { reclassSweep = null; requestRender(); }
    if (extending) { extending.txn.abort(); extending = null; requestRender(); }
    if (stroke) { stroke.txn.abort(); stroke = null; requestRender(); }
    if (grabbed) {
      const n = graph.nodes.get(grabbed.id);
      if (n) { n.x = grabbed.ox; n.y = grabbed.oy; }
      grabbed = null;
      requestRender();
    }
    for (const drag of [softDrag, groupDrag]) {
      if (!drag) continue;
      for (const [i, o] of drag.originals) {
        const n = graph.nodes.get(i);
        if (n) { n.x = o.x; n.y = o.y; }
      }
    }
    if (softDrag || groupDrag) {
      softDrag = null;
      groupDrag = null;
      requestRender();
    }
  }

  function onPointerDown(e: PointerEvent): void {
    lastActivity = Date.now();
    canvas.setPointerCapture(e.pointerId);
    const p = eventPos(e);
    pointers.set(e.pointerId, { sx: p.sx, sy: p.sy });
    if (menuOpen) toggleMenu(false);

    if (pointers.size === 2) {
      cancelStroke();
      panning = null;
      clickCandidate = null;
      const [p1, p2] = [...pointers.values()];
      pinch = { dist: Math.hypot(p1.sx - p2.sx, p1.sy - p2.sy), mx: (p1.sx + p2.sx) / 2, my: (p1.sy + p2.sy) / 2 };
      return;
    }
    if (pointers.size > 2) return;

    if (e.button === 2) {
      // right-drag pans; a plain right-click (no movement) ends the chain on pointerup.
      // Don't rely on the contextmenu event for this: macOS/Linux fire it already on
      // button press, i.e. before a drag can be told apart from a click.
      rightMoved = false;
      panning = { sx: p.sx, sy: p.sy };
      return;
    }
    const panButton = e.button === 1 || spaceHeld || (readOnly && mode !== "measure" && mode !== "flag");
    if (panButton) {
      panning = { sx: p.sx, sy: p.sy };
      return;
    }

    // grid-edit state: clicks toggle cells, drags pan — tools are suspended
    if (gridOn && gridEdit) {
      clickCandidate = { sx: p.sx, sy: p.sy, button: e.button, mod: false };
      panning = { sx: p.sx, sy: p.sy };
      return;
    }

    // labels hidden (V): no blind edits — the pointer pans instead, like read-only
    if (!readOnly && !showLabels && mode !== "measure") {
      labelsHiddenGuard();
      panning = { sx: p.sx, sy: p.sy };
      return;
    }

    // pen eraser
    if (e.pointerType === "pen" && (e.buttons & 32) !== 0) {
      erasing = new Txn(graph);
      if (deleteAt(graph, erasing, p.x, p.y, snapDistImage() * 1.5)) touch();
      return;
    }

    switch (mode) {
      case "paint": {
        if (!inImageBounds(p.x, p.y)) {
          outsideToast();
          break;
        }
        const txn = new Txn(graph);
        const anchor = resolveAnchor(graph, txn, p.x, p.y, snapDistImage());
        stroke = { txn, last: anchor };
        requestRender();
        break;
      }
      case "soft": {
        softDrag = { originals: new Map(), lastX: p.x, lastY: p.y, sx0: p.sx, sy0: p.sy };
        break;
      }
      case "extend": {
        extending = { txn: new Txn(graph), count: 0, tried: new Set() };
        processExtend(p.x, p.y);
        break;
      }
      case "move": {
        const nid = nearestNode(graph, p.x, p.y, Math.max(snapDistImage(), 12 / viewport.scale));
        if (nid !== null) {
          if (selectedNodes.has(nid) && selectedNodes.size > 1) {
            const originals = new Map<number, { x: number; y: number }>();
            for (const i of selectedNodes) {
              const n = graph.nodes.get(i);
              if (n) originals.set(i, { x: n.x, y: n.y });
            }
            groupDrag = { originals, lastX: p.x, lastY: p.y };
          } else {
            selectedNodes.clear();
            const n = graph.nodes.get(nid)!;
            grabbed = { id: nid, ox: n.x, oy: n.y };
          }
        } else {
          clickCandidate = { sx: p.sx, sy: p.sy, button: e.button, mod: false }; // click clears selection, drag = lasso
        }
        break;
      }
      default:
        // draw: click on pointerup, drag = pan; delete/move: drag = lasso; reclass: drag = stencil sweep
        clickCandidate = { sx: p.sx, sy: p.sy, button: e.button, mod: e.shiftKey || e.ctrlKey };
        if (mode === "delete" || mode === "reclass" || mode === "flag") break; // drag = lasso / stencil
        panning = { sx: p.sx, sy: p.sy };
    }
  }

  function onPointerMove(e: PointerEvent): void {
    const p = eventPos(e);
    cursor = { ...p };
    const prev = pointers.get(e.pointerId);
    if (prev) pointers.set(e.pointerId, { sx: p.sx, sy: p.sy });

    if (pinch && pointers.size === 2) {
      const [p1, p2] = [...pointers.values()];
      const dist = Math.hypot(p1.sx - p2.sx, p1.sy - p2.sy);
      const mx = (p1.sx + p2.sx) / 2;
      const my = (p1.sy + p2.sy) / 2;
      if (pinch.dist > 0 && dist > 0) viewport.zoomAt(mx, my, dist / pinch.dist);
      viewport.panBy(mx - pinch.mx, my - pinch.my);
      pinch = { dist, mx, my };
      requestRender();
      return;
    }

    if (panning && prev) {
      if (clickCandidate && Math.hypot(p.sx - clickCandidate.sx, p.sy - clickCandidate.sy) > 5) {
        clickCandidate = null; // became a drag-pan
      }
      if (!clickCandidate) {
        if (Math.hypot(p.sx - panning.sx, p.sy - panning.sy) > 4) rightMoved = true;
        viewport.panBy(p.sx - prev.sx, p.sy - prev.sy);
        requestRender();
        return;
      }
    }

    if (prev && !readOnly && mode === "reclass" && !panning && !erasing) {
      if (clickCandidate && Math.hypot(p.sx - clickCandidate.sx, p.sy - clickCandidate.sy) > 5) {
        const [ix, iy] = viewport.toImage(clickCandidate.sx, clickCandidate.sy);
        reclassSweep = { touched: new Set() };
        clickCandidate = null;
        lastReclass = null;
        processReclassSweep(ix, iy);
      }
      if (reclassSweep) {
        lastActivity = Date.now();
        processReclassSweep(p.x, p.y);
        maintainAutoPan();
        requestRender();
        return;
      }
    }

    if (prev && ((!readOnly && (mode === "delete" || mode === "move")) || mode === "flag") && !panning && !erasing && !grabbed && !groupDrag) {
      if (clickCandidate && Math.hypot(p.sx - clickCandidate.sx, p.sy - clickCandidate.sy) > 5) {
        const [ix, iy] = viewport.toImage(clickCandidate.sx, clickCandidate.sy);
        lassoAdditive = clickCandidate.mod;
        lasso = [{ x: ix, y: iy }];
        clickCandidate = null;
      }
      if (lasso) {
        lastActivity = Date.now();
        const last = lasso[lasso.length - 1];
        if (Math.hypot(p.x - last.x, p.y - last.y) * viewport.scale > 3) lasso.push({ x: p.x, y: p.y });
        maintainAutoPan();
        requestRender();
        return;
      }
    }

    if (erasing) {
      lastActivity = Date.now();
      if (deleteAt(graph, erasing, p.x, p.y, snapDistImage() * 1.5)) touch();
      return;
    }

    if (extending) {
      lastActivity = Date.now();
      processExtend(p.x, p.y);
      requestRender();
      return;
    }

    if (softDrag) {
      lastActivity = Date.now();
      const dx = p.x - softDrag.lastX;
      const dy = p.y - softDrag.lastY;
      const radius = settings.soft_radius / viewport.scale;
      if (radius > 0 && (dx !== 0 || dy !== 0)) {
        for (const n of graph.nodes.values()) {
          const d = Math.hypot(n.x - p.x, n.y - p.y);
          if (d >= radius) continue;
          const w = Math.pow(1 - d / radius, settings.soft_falloff);
          if (!softDrag.originals.has(n.i)) softDrag.originals.set(n.i, { x: n.x, y: n.y });
          n.x += dx * w;
          n.y += dy * w;
        }
      }
      softDrag.lastX = p.x;
      softDrag.lastY = p.y;
      requestRender();
      return;
    }

    if (groupDrag) {
      lastActivity = Date.now();
      const dx = p.x - groupDrag.lastX;
      const dy = p.y - groupDrag.lastY;
      for (const i of groupDrag.originals.keys()) {
        const n = graph.nodes.get(i);
        if (n) { n.x += dx; n.y += dy; }
      }
      groupDrag.lastX = p.x;
      groupDrag.lastY = p.y;
      maintainAutoPan();
      requestRender();
      return;
    }

    if (stroke) {
      lastActivity = Date.now();
      const events = e.getCoalescedEvents ? e.getCoalescedEvents() : [e];
      for (const ev of events) {
        const q = eventPos(ev as PointerEvent);
        extendStroke(q.x, q.y);
        if (!stroke) break;
      }
      maintainAutoPan();
      requestRender();
      return;
    }

    if (grabbed) {
      lastActivity = Date.now();
      const n = graph.nodes.get(grabbed.id);
      if (n) { n.x = p.x; n.y = p.y; }
      maintainAutoPan();
      requestRender();
      return;
    }

    requestRender(); // hover indicators / preview line
  }

  function onPointerUp(e: PointerEvent): void {
    const p = eventPos(e);
    pointers.delete(e.pointerId);
    if (pinch) {
      if (pointers.size < 2) pinch = null;
      return;
    }

    if (reclassSweep) {
      finishReclassSweep();
      reclassSweep = null;
      clickCandidate = null;
      panning = null;
      requestRender();
      return;
    }

    if (lasso) {
      applyLasso(lasso);
      lasso = null;
      clickCandidate = null;
      panning = null;
      return;
    }

    if (erasing) {
      erasing.commit();
      erasing = null;
      touch();
      return;
    }

    if (extending) {
      const count = extending.count;
      extending.txn.commit();
      extending = null;
      if (count > 0) {
        toast(`${count} line end${count === 1 ? "" : "s"} connected`);
        touch();
      }
      return;
    }

    if (softDrag || groupDrag) {
      const softClick = softDrag !== null && Math.hypot(p.sx - softDrag.sx0, p.sy - softDrag.sy0) < 5;
      const drag = (softDrag ?? groupDrag)!;
      const txn = new Txn(graph);
      for (const [i, o] of drag.originals) {
        const n = graph.nodes.get(i);
        if (n && (n.x !== o.x || n.y !== o.y)) {
          // re-applying the current position is a no-op; this just records the op
          txn.do({ t: "moveNode", i, from: { x: o.x, y: o.y }, to: { x: n.x, y: n.y } });
        }
      }
      if (txn.ops.length && settings.snap_intersections) {
        resolveCrossings(graph, txn, [...drag.originals.keys()]);
      }
      const changed = txn.commit();
      softDrag = null;
      groupDrag = null;
      if (changed) touch();
      else if (softClick) tryInsertVertex(p.x, p.y); // stationary tap: double-click inserts a vertex
      return;
    }

    if (stroke) {
      const txn = stroke.txn;
      const last = stroke.last;
      const lastN = graph.nodes.get(last);
      // close the stroke: snap the end to nearby geometry
      if (lastN) {
        const sd = snapDistImage();
        const target = sd > 0 ? nearestNode(graph, p.x, p.y, sd, new Set([last])) : null;
        if (target !== null) {
          insertEdge(graph, txn, last, target, currentClass, settings.snap_intersections);
        } else if (Math.hypot(p.x - lastN.x, p.y - lastN.y) >= Math.min(4, settings.node_spacing / 4)) {
          const hit = sd > 0 ? nearestEdge(graph, p.x, p.y, sd) : null;
          const endId = hit ? txn.splitEdge(hit.edgeId, hit.x, hit.y) : txn.addNode(p.x, p.y);
          insertEdge(graph, txn, last, endId, currentClass, settings.snap_intersections);
        }
      }
      if (!txn.commit()) { /* empty stroke */ }
      stroke = null;
      touch();
      return;
    }

    if (grabbed) {
      const txn = new Txn(graph);
      const n = graph.nodes.get(grabbed.id);
      if (n) {
        const mergeTarget = nearestNode(graph, n.x, n.y, snapDistImage(), new Set([grabbed.id]));
        if (mergeTarget !== null) {
          // restore original position first so the move is fully captured by ops
          const fx = n.x, fy = n.y;
          txn.do({ t: "moveNode", i: grabbed.id, from: { x: grabbed.ox, y: grabbed.oy }, to: { x: fx, y: fy } });
          mergeNodes(graph, txn, grabbed.id, mergeTarget);
        } else if (n.x !== grabbed.ox || n.y !== grabbed.oy) {
          txn.do({ t: "moveNode", i: grabbed.id, from: { x: grabbed.ox, y: grabbed.oy }, to: { x: n.x, y: n.y } });
        }
        if (txn.ops.length && settings.snap_intersections) {
          // dragging a line across another must leave a shared junction behind
          resolveCrossings(graph, txn, [mergeTarget ?? grabbed.id]);
        }
      }
      txn.commit();
      grabbed = null;
      touch();
      return;
    }

    if (e.button === 2) {
      panning = null;
      if (!rightMoved && mode === "measure" && measurePts.length) {
        // right-click ends the tape: freeze it (< 2 points is no measurement — clear)
        measureDone = measurePts.length >= 2;
        if (!measureDone) measurePts = [];
        requestRender();
      }
      if (!rightMoved && activeNode !== null) { activeNode = null; requestRender(); }
      if (!rightMoved && mode === "flag" && flagChain) finishChain(false);
      return;
    }

    if (clickCandidate && e.button === clickCandidate.button) {
      const btn = clickCandidate.button;
      const mod = clickCandidate.mod;
      clickCandidate = null;
      panning = null;
      if (btn === 0) handleClick(p.x, p.y, mod);
      return;
    }
    panning = null;
  }

  function applyLasso(poly: { x: number; y: number }[]): void {
    if (poly.length < 3) return;
    lastActivity = Date.now();
    const insideNodes = new Set<number>();
    for (const n of graph.nodes.values()) {
      if (pointInPolygon(poly, n.x, n.y)) insideNodes.add(n.i);
    }
    if (mode === "flag") {
      if (!(!readOnly || canAdminFlag)) return; // nothing to flag with
      if (!lassoAdditive) flagSel.clear();
      // an edge is selected when its midpoint lies inside the lasso — so parts
      // of a line can be picked without catching the neighbours
      for (const e of graph.edges.values()) {
        const a = graph.nodes.get(e.a), b = graph.nodes.get(e.b);
        if (a && b && pointInPolygon(poly, (a.x + b.x) / 2, (a.y + b.y) / 2)) flagSel.add(e.i);
      }
      if (flagSel.size) toast(`${flagSel.size} segment${flagSel.size === 1 ? "" : "s"} selected — Enter (or click the selection) to leave a note on them, Shift+drag adds more`);
      requestRender();
      return;
    }
    if (mode === "move") {
      selectedNodes.clear();
      for (const i of insideNodes) selectedNodes.add(i);
      if (selectedNodes.size) toast(`${selectedNodes.size} node${selectedNodes.size === 1 ? "" : "s"} selected — drag one of them to move together`);
      requestRender();
      return;
    }
    const txn = new Txn(graph);
    let count = 0;
    if (mode === "delete") {
      for (const e of [...graph.edges.values()]) {
        if (insideNodes.has(e.a) && insideNodes.has(e.b)) {
          txn.delEdge(e.i);
          count++;
        }
      }
      txn.pruneIsolated([...insideNodes]);
      if (txn.commit()) {
        toast(`${count} edge${count === 1 ? "" : "s"} deleted`);
        touch();
      }
    }
    requestRender();
  }

  function nearestFlag(x: number, y: number, r: number): number | null {
    let best: number | null = null;
    let bestD = r * r;
    for (const f of [...graph.flags.values(), ...adminFlags.values()]) {
      const d = (f.x - x) ** 2 + (f.y - y) ** 2;
      if (d <= bestD) {
        bestD = d;
        best = f.i;
      }
    }
    return best;
  }

  function distToSeg(px: number, py: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1, dy = y2 - y1;
    const l2 = dx * dx + dy * dy;
    const t = l2 ? Math.max(0, Math.min(1, ((px - x1) * dx + (py - y1) * dy) / l2)) : 0;
    return Math.hypot(px - (x1 + t * dx), py - (y1 + t * dy));
  }

  /** The flag under the cursor: its marker first, else its shape (inside an
      area, near a line or a flagged segment). */
  function hitFlag(x: number, y: number): number | null {
    const m = nearestFlag(x, y, 24 / viewport.scale);
    if (m !== null) return m;
    const tol = 8 / viewport.scale;
    for (const f of [...graph.flags.values(), ...adminFlags.values()]) {
      const sh = f.shape;
      if (!sh || !shapeShown(f)) continue;
      if (sh.kind === "area") {
        if (pointInPolygon(sh.pts.map(([px, py]) => ({ x: px, y: py })), x, y)) return f.i;
      } else if (sh.kind === "line") {
        for (let k = 1; k < sh.pts.length; k++) {
          if (distToSeg(x, y, sh.pts[k - 1][0], sh.pts[k - 1][1], sh.pts[k][0], sh.pts[k][1]) <= tol) return f.i;
        }
      } else {
        for (const [x1, y1, x2, y2] of sh.segs) {
          if (distToSeg(x, y, x1, y1, x2, y2) <= tol) return f.i;
        }
      }
    }
    return null;
  }

  /** Shift+click chain finished: closed → area flag, open → line flag. */
  function finishChain(closed: boolean): void {
    const pts = flagChain ?? [];
    flagChain = null;
    requestRender();
    if (pts.length < (closed ? 3 : 2)) {
      if (pts.length) toast(closed ? "An area needs at least three points" : "A line needs at least two points");
      return;
    }
    const shape: FlagShape = { kind: closed ? "area" : "line", pts: pts.map((q) => [q.x, q.y]) };
    flagDialog(null, { x: pts[0].x, y: pts[0].y }, shape);
  }

  /** Note on the lasso-selected segments: snapshot of their geometry. */
  function openSelectionDialog(): void {
    const segs: [number, number, number, number][] = [];
    for (const id of flagSel) {
      const e = graph.edges.get(id);
      const a = e && graph.nodes.get(e.a), b = e && graph.nodes.get(e.b);
      if (a && b) segs.push([a.x, a.y, b.x, b.y]);
    }
    if (!segs.length) { flagSel.clear(); requestRender(); return; }
    flagDialog(null, { x: segs[0][0], y: segs[0][1] }, { kind: "labels", segs });
  }

  function hitSelection(x: number, y: number): boolean {
    if (!flagSel.size) return false;
    const hit = nearestEdge(graph, x, y, 10 / viewport.scale);
    return hit !== null && flagSel.has(hit.edgeId);
  }

  // replies are payload state (own table server-side), not part of the graph
  const flagReplies = payload.assignment.flag_replies ?? {};
  const canReply = payload.assignment.id > 0 && (isOwner || user.is_admin);
  // admin flags: planted by admins on someone else's tile (own table server-side,
  // negative ids) — not undoable, not part of the graph or the autosave
  const adminFlags = new Map<number, AdminFlag>((payload.assignment.admin_flags ?? []).map((f) => [f.i, f]));
  const canAdminFlag = user.is_admin && !isOwner && payload.assignment.id > 0;

  // ---- flag life cycle, derived from the thread (docs/flags.md; mirrors server flags.py)
  type FlagState = "open" | "answered" | "resolved";
  const ownerId = payload.assignment.owner?.id ?? user.id;
  const flagAuthorId = (i: number): number => (i < 0 ? adminFlags.get(i)?.user_id ?? -1 : ownerId);
  const flagThread = (i: number): FlagReply[] => flagReplies[String(i)] ?? [];
  /** Remove a graph flag without touching the undo history: used for flags an
      admin deleted server-side (own action, or learned through the heartbeat). */
  function dropFlag(i: number): void {
    graph.flags.delete(i);
    delete flagReplies[String(i)];
  }
  function flagState(i: number): FlagState {
    const t = flagThread(i);
    if (!t.length) return "open";
    const last = t[t.length - 1];
    if (last.kind === "resolved") return "resolved";
    return last.user_id !== flagAuthorId(i) ? "answered" : "open";
  }
  /** Unresolved, and someone else had the last word (for a fresh flag: its author). */
  function waitingForMe(i: number): boolean {
    if (flagState(i) === "resolved") return false;
    const t = flagThread(i);
    return t.length ? t[t.length - 1].user_id !== user.id : flagAuthorId(i) !== user.id;
  }
  function flagColor(i: number): string {
    const st = flagState(i);
    return st === "resolved" ? "#2ee65e" : st === "answered" ? "#4cc9f0" : i < 0 ? "#ffb400" : "#ff5964";
  }
  const STATE_LABEL: Record<FlagState, string> = { open: "open", answered: "answered", resolved: "resolved" };
  const allFlagIds = (): number[] => [...graph.flags.keys(), ...adminFlags.keys()];
  /** Cycle order for ‹ ›: flags waiting for you first, then the rest, each by id. */
  function flagOrder(): number[] {
    const ids = allFlagIds();
    const byId = (a: number, b: number) => Math.abs(a) - Math.abs(b) || a - b;
    return [...ids.filter(waitingForMe).sort(byId), ...ids.filter((i) => !waitingForMe(i)).sort(byId)];
  }
  const flagAt = (i: number): GraphFlag | AdminFlag | undefined => graph.flags.get(i) ?? adminFlags.get(i);
  function centerOn(x: number, y: number): void {
    const r = canvas.getBoundingClientRect();
    viewport.tx = r.width / 2 - x * viewport.scale;
    viewport.ty = r.height / 2 - y * viewport.scale;
    requestRender();
  }
  function updateFlagChip(): void {
    const n = allFlagIds().filter(waitingForMe).length;
    flagChip.textContent = `⚑ ${n} for you`;
    flagChip.style.display = n ? "" : "none";
    if (!flagChip.isConnected && modalityChip.parentNode) modalityChip.before(flagChip);
  }
  function openWaitingFlag(): void {
    const id = flagOrder()[0];
    const f = id === undefined ? undefined : flagAt(id);
    if (!f) return;
    if (mode !== "flag") setMode("flag");
    if (!showLabels) toggleLabels();
    centerOn(f.x, f.y);
    flagDialog(f.i);
  }

  /** Note dialog for an existing flag (read/edit/remove/reply/resolve, ‹ › to
      cycle) or a new one at pos, optionally with a shape (area / line / labels).
      Handles both the labeler's own graph flags and admin flags (i < 0): graph
      flags are edited through the undoable Txn, admin flags through their own
      endpoints (admins only — including on tiles they view read-only). */
  function flagDialog(flagId: number | null, pos?: { x: number; y: number }, shape?: FlagShape): void {
    const adminExisting = flagId !== null && flagId < 0 ? adminFlags.get(flagId) : undefined;
    const existing = flagId !== null && flagId >= 0 ? graph.flags.get(flagId) : undefined;
    const anyExisting = existing ?? adminExisting;
    // a new flag is a graph flag for the working owner, an admin flag for an admin
    const newAsAdmin = !anyExisting && canAdminFlag;
    if (!anyExisting && (!pos || (readOnly && !newAsAdmin))) return;
    // may this user change the note text?
    const editable = adminExisting ? user.is_admin : anyExisting ? !readOnly : true;
    // admins may remove any flag — a labeler's graph flag on a tile they only
    // view read-only goes through its own endpoint (the graph is not theirs to save)
    const removable = !!anyExisting && (editable || (user.is_admin && payload.assignment.id > 0));
    const theShape: FlagShape | undefined = anyExisting ? anyExisting.shape ?? undefined : shape;
    const kindLabel = !theShape ? "spot"
      : theShape.kind === "area" ? "area"
      : theShape.kind === "line" ? "drawn line (not a label)"
      : `${theShape.segs.length} label segment${theShape.segs.length === 1 ? "" : "s"}`;
    const state = anyExisting ? flagState(anyExisting.i) : null;
    const ta = h("textarea", {
      rows: 5,
      placeholder: newAsAdmin ? "What should be fixed or looked at here?" : "What should be discussed here?",
    });
    ta.value = anyExisting?.text ?? "";

    const post = async (body: { text?: string; kind?: "message" | "resolved" }): Promise<FlagReply> => {
      const r = await api<{ reply: FlagReply }>(
        `/api/assignments/${payload.assignment.id}/flags/${anyExisting!.i}/reply`,
        { method: "POST", body },
      );
      (flagReplies[String(anyExisting!.i)] ??= []).push(r.reply);
      updateFlagChip();
      requestRender(); // colors follow the thread right away
      return r.reply;
    };
    const entryRow = (r: FlagReply) => r.kind === "resolved"
      ? h("div", { className: "flag-reply muted" }, `✓ resolved by ${r.username} · ${new Date(r.created_at * 1000).toLocaleDateString()}`)
      : h("div", { className: "flag-reply" },
          h("span", { className: "muted" }, `${r.username} · ${new Date(r.created_at * 1000).toLocaleDateString()}`),
          h("div", { className: "flag-note" }, r.text));

    let replySection: HTMLElement | null = null;
    if (anyExisting) {
      const replies = flagThread(anyExisting.i);
      const list = h("div", { className: "flag-replies" }, ...replies.map(entryRow));
      let replyRow: HTMLElement | null = null;
      if (canReply) {
        const input = h("input", { placeholder: state === "resolved" ? "Reply (reopens the flag)…" : "Reply…" });
        const send = async () => {
          const text = input.value.trim();
          if (!text) return;
          try {
            list.append(entryRow(await post({ text })));
            input.value = "";
          } catch (e: any) {
            toast(`Could not reply: ${e.message}`, true);
          }
        };
        input.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); void send(); } });
        replyRow = h("div", { className: "row gap flag-reply-row" },
          input, h("button", { className: "btn btn-small", onClick: () => void send() }, "Reply"),
          state !== "resolved"
            ? h("button", { className: "btn btn-small", title: "Close this flag: it turns green and stops waiting for anyone. A new reply reopens it.",
                onClick: async () => {
                  try {
                    await post({ kind: "resolved" });
                    close();
                    toast("Flag resolved");
                  } catch (e: any) {
                    toast(`Could not resolve: ${e.message}`, true);
                  }
                } }, "Mark resolved")
            : null);
      }
      if (replies.length || replyRow) {
        replySection = h("div", { className: "flag-reply-section" }, list, replyRow);
      }
    }

    const isAdminFlag = !!adminExisting || newAsAdmin;
    const removeFlag = async () => {
      if (adminExisting) {
        try {
          await api(`/api/assignments/${payload.assignment.id}/admin-flags/${adminExisting.i}`, { method: "DELETE" });
        } catch (e: any) {
          toast(`Could not remove: ${e.message}`, true);
          return;
        }
        adminFlags.delete(adminExisting.i);
        close();
        toast("Admin flag removed");
      } else if (existing && !editable) {
        // admin on someone else's active tile: server-side removal, replayed on the owner's saves
        try {
          await api(`/api/assignments/${payload.assignment.id}/flags/${existing.i}`, { method: "DELETE" });
        } catch (e: any) {
          toast(`Could not remove: ${e.message}`, true);
          return;
        }
        dropFlag(existing.i);
        close();
        toast("Flag removed");
      } else if (existing) {
        const txn = new Txn(graph);
        txn.delFlag(existing.i);
        txn.commit();
        close();
        toast("Flag removed (Ctrl+Z to undo)");
        touch();
      }
      updateFlagChip();
      requestRender();
    };
    const saveFlag = async () => {
      const text = ta.value.trim();
      if (isAdminFlag) {
        try {
          if (adminExisting) {
            await api(`/api/assignments/${payload.assignment.id}/admin-flags/${adminExisting.i}`,
              { method: "PUT", body: { text } });
            adminExisting.text = text;
          } else {
            const r = await api<{ flag: AdminFlag }>(
              `/api/assignments/${payload.assignment.id}/admin-flags`,
              { method: "POST", body: { x: pos!.x, y: pos!.y, text, shape: theShape ?? null } });
            adminFlags.set(r.flag.i, r.flag);
          }
        } catch (e: any) {
          toast(`Could not save: ${e.message}`, true);
          return;
        }
        close();
        toast(adminExisting ? "Note updated" : "Admin flag planted — the labeler sees it in orange");
      } else {
        const txn = new Txn(graph);
        if (existing) txn.setFlagText(existing.i, text);
        else txn.addFlag(pos!.x, pos!.y, text, theShape);
        close();
        if (txn.commit()) {
          toast(existing ? "Note updated" : theShape ? "Flag added" : "Spot flagged");
          touch();
        }
      }
      if (!anyExisting) flagSel.clear(); // the selection became a flag
      updateFlagChip();
      requestRender();
    };

    // ‹ › cycle through the tile's flags, centering the view on each
    let nav: HTMLElement | null = null;
    const order = anyExisting ? flagOrder() : [];
    const idx = anyExisting ? order.indexOf(anyExisting.i) : -1;
    const goto = (step: number) => {
      if (order.length < 2) return;
      const id = order[(idx + step + order.length) % order.length];
      const f = flagAt(id);
      if (!f) return;
      close();
      centerOn(f.x, f.y);
      flagDialog(id);
    };
    if (anyExisting && order.length > 1) {
      nav = h("div", { className: "flag-nav" },
        h("button", { className: "btn btn-small", title: "Previous flag (PageUp)", onClick: () => goto(-1) }, "‹"),
        h("span", { className: "muted" }, `${idx + 1} of ${order.length}` + (waitingForMe(anyExisting.i) ? " · waiting for you" : "")),
        h("button", { className: "btn btn-small", title: "Next flag (PageDown)", onClick: () => goto(1) }, "›"));
    }

    const stateBadge = state
      ? h("span", { className: "flag-state", style: { background: flagColor(anyExisting!.i) } }, STATE_LABEL[state])
      : null;
    const box = h(
      "div",
      { className: "flag-dialog" },
      h("h2", { className: "row gap" },
        h("span", { className: "spacer" }, adminExisting ? "Admin flag" : newAsAdmin ? "Flag this (as admin)"
          : existing ? "Flagged" : "Flag this"),
        stateBadge),
      h("p", { className: "flag-kind" },
        kindLabel + (adminExisting ? ` · ${adminExisting.username} · ${new Date(adminExisting.created_at * 1000).toLocaleDateString()}` : "")),
      editable
        ? ta
        : h("p", { className: "flag-note" }, anyExisting?.text || h("span", { className: "muted" }, "(no note)")),
      replySection,
      h(
        "div",
        { className: "row gap right" },
        removable
          ? h("button", { className: "btn btn-danger", onClick: () => void removeFlag() }, "Remove flag")
          : null,
        h("button", { className: "btn", onClick: () => close() }, editable ? "Cancel" : "Close"),
        editable
          ? h("button", { className: "btn btn-primary", onClick: () => void saveFlag() },
              anyExisting ? "Save note" : "Add flag")
          : null,
      ),
      nav,
    );
    // docked in the corner, no dimming: the flag (centered by ‹ ›) stays visible
    const close = modal(box, undefined, { dock: "bottom-right" });
    // PageUp/PageDown cycle; listen on the modal element itself, which holds
    // focus when there is no textarea to focus (read-only dialogs)
    box.parentElement!.addEventListener("keydown", (e) => {
      if (e.key === "PageUp") { e.preventDefault(); goto(-1); }
      else if (e.key === "PageDown") { e.preventDefault(); goto(1); }
    });
    if (editable) ta.focus();
  }

  /** Double-clicking an edge (move / soft move) inserts a vertex there. */
  function tryInsertVertex(x: number, y: number): void {
    const hit = nearestEdge(graph, x, y, Math.max(snapDistImage(), 8 / viewport.scale));
    const now = Date.now();
    if (hit && lastVertexClick && lastVertexClick.edgeId === hit.edgeId && now - lastVertexClick.t < 450) {
      const txn = new Txn(graph);
      txn.splitEdge(hit.edgeId, hit.x, hit.y);
      txn.commit();
      lastVertexClick = null;
      toast("Vertex inserted — drag it into place");
      touch();
    } else {
      lastVertexClick = hit ? { t: now, edgeId: hit.edgeId } : null;
    }
  }

  /** Shift/Ctrl+click in delete mode: remove a pass-through vertex and bridge
      its two segments, so the line stretches straight across the gap. */
  function dissolveVertexAt(x: number, y: number): void {
    const nid = nearestNode(graph, x, y, Math.max(snapDistImage(), 8 / viewport.scale));
    if (nid === null) return;
    if (graph.degree(nid) !== 2) {
      toast("Only a vertex with exactly two segments can be dissolved");
      return;
    }
    const [e1, e2] = graph.edgesOf(nid);
    const n1 = e1.a === nid ? e1.b : e1.a;
    const n2 = e2.a === nid ? e2.b : e2.a;
    const txn = new Txn(graph);
    txn.delNodeWithEdges(nid);
    if (n1 !== n2) txn.addEdge(n1, n2, e1.c);
    txn.pruneIsolated([n1, n2]);
    if (txn.commit()) touch();
  }

  function handleClick(x: number, y: number, mod = false): void {
    lastActivity = Date.now();
    if (gridOn && gridEdit) {
      const cw = payload.ref.width / gridState.nx;
      const ch = payload.ref.height / gridState.ny;
      const cxi = Math.floor(x / cw);
      const cyi = Math.floor(y / ch);
      if (cxi >= 0 && cyi >= 0 && cxi < gridState.nx && cyi < gridState.ny) {
        const idx = cyi * gridState.nx + cxi;
        if (gridChecked.has(idx)) gridChecked.delete(idx);
        else gridChecked.add(idx);
        saveGrid();
        requestRender();
      }
      return;
    }
    if (mode === "draw") {
      if (!inImageBounds(x, y)) {
        outsideToast();
        return;
      }
      const txn = new Txn(graph);
      const target = resolveAnchor(graph, txn, x, y, snapDistImage());
      if (activeNode !== null && graph.nodes.has(activeNode) && target !== activeNode) {
        insertEdge(graph, txn, activeNode, target, currentClass, settings.snap_intersections);
      }
      txn.commit();
      activeNode = target;
      touch();
    } else if (mode === "delete") {
      if (mod) {
        dissolveVertexAt(x, y);
        return;
      }
      const txn = new Txn(graph);
      if (deleteAt(graph, txn, x, y, Math.max(snapDistImage(), 8 / viewport.scale))) {
        txn.commit();
        touch();
      }
    } else if (mode === "measure") {
      if (measureDone) { measurePts = []; measureDone = false; } // ended tape: click starts a new one
      measurePts.push({ x, y });
      requestRender();
    } else if (mode === "flag") {
      if (labelsHiddenGuard()) return; // flags are hidden along with the labels
      const canPlace = !readOnly || canAdminFlag;
      if (mod || flagChain) {
        // Shift+click (or any click once a chain is going): chain a shape;
        // a click back at the first vertex closes it into an area
        if (!canPlace) return;
        if (flagChain && flagChain.length >= 3
            && Math.hypot(x - flagChain[0].x, y - flagChain[0].y) <= 12 / viewport.scale) {
          finishChain(true);
          return;
        }
        if (!inImageBounds(x, y)) { outsideToast(); return; }
        (flagChain ??= []).push({ x, y });
        if (flagChain.length === 1) toast("Shift+click more points — click the first point to close an area, right-click or Enter to end a line, Esc to cancel");
        requestRender();
        return;
      }
      if (hitSelection(x, y)) { openSelectionDialog(); return; }
      const fid = hitFlag(x, y);
      if (fid !== null) flagDialog(fid);
      else if (flagSel.size) { flagSel.clear(); requestRender(); } // click on empty ground drops the selection
      else if (canPlace) {
        if (!inImageBounds(x, y)) {
          outsideToast();
          return;
        }
        flagDialog(null, { x, y });
      }
    } else if (mode === "move") {
      selectedNodes.clear();
      tryInsertVertex(x, y);
      requestRender();
    } else if (mode === "join") {
      const radius = settings.soft_radius / viewport.scale;
      const ids: number[] = [];
      for (const n of graph.nodes.values()) {
        if (Math.hypot(n.x - x, n.y - y) <= radius) ids.push(n.i);
      }
      if (ids.length < 2) return;
      const txn = new Txn(graph);
      const keep = ids[0];
      const kn = graph.nodes.get(keep)!;
      txn.do({ t: "moveNode", i: keep, from: { x: kn.x, y: kn.y }, to: { x, y } });
      for (const i of ids.slice(1)) mergeNodes(graph, txn, i, keep);
      txn.commit();
      toast(`${ids.length} nodes joined`);
      touch();
    } else if (mode === "reclass") {
      const hit = nearestEdge(graph, x, y, Math.max(snapDistImage(), 8 / viewport.scale));
      if (!hit) { lastReclass = null; return; }
      const now = Date.now();
      if (lastReclass && lastReclass.edgeId === hit.edgeId && now - lastReclass.t < 450) {
        // double-click: reassign the whole connected component that had the original class
        if (lastReclass.committed) graph.undo(); // revert the single-edge reassignment from the first click
        const fromClass = lastReclass.fromClass;
        const txn = new Txn(graph);
        const seenEdges = new Set<number>([hit.edgeId]);
        const queue: number[] = [];
        const start = graph.edges.get(hit.edgeId);
        if (start) queue.push(start.a, start.b);
        txn.setClass(hit.edgeId, currentClass);
        while (queue.length) {
          const nid = queue.pop()!;
          for (const e of graph.edgesOf(nid)) {
            if (!seenEdges.has(e.i) && e.c === fromClass) {
              seenEdges.add(e.i);
              txn.setClass(e.i, currentClass);
              queue.push(e.a === nid ? e.b : e.a);
            }
          }
        }
        txn.commit();
        lastReclass = null;
        touch();
      } else {
        const e = graph.edges.get(hit.edgeId)!;
        lastReclass = { t: now, edgeId: hit.edgeId, fromClass: e.c, committed: e.c !== currentClass };
        if (e.c !== currentClass) {
          const txn = new Txn(graph);
          txn.setClass(hit.edgeId, currentClass);
          txn.commit();
          touch();
        }
      }
    }
  }

  function onWheel(e: WheelEvent): void {
    e.preventDefault();
    const r = canvas.getBoundingClientRect();
    const sx = e.clientX - r.left;
    const sy = e.clientY - r.top;
    const delta = e.deltaMode === 1 ? e.deltaY * 16 : e.deltaY;
    if (e.ctrlKey && (mode === "soft" || mode === "join" || mode === "extend" || mode === "reclass") && !readOnly) {
      // Ctrl+scroll resizes the soft-move brush
      settings.soft_radius = Math.max(20, Math.min(300, Math.round(settings.soft_radius * Math.exp(-delta * 0.002))));
      saveSettings();
      const [x, y] = viewport.toImage(sx, sy);
      cursor = { sx, sy, x, y };
      requestRender();
      return;
    }
    viewport.zoomAt(sx, sy, Math.exp(-delta * 0.0018));
    requestRender();
  }

  function onKeyDown(e: KeyboardEvent): void {
    if (view3dOpen) return; // the 3D view owns the keyboard (fly controls)
    const target = e.target as HTMLElement;
    if (target.tagName === "INPUT" || target.tagName === "SELECT" || target.tagName === "TEXTAREA") return;
    lastActivity = Date.now();
    if (e.key === " ") {
      spaceHeld = true;
      e.preventDefault();
      return;
    }
    if (e.key === "Tab") {
      e.preventDefault();
      cycleModality(e.shiftKey ? -1 : 1);
      return;
    }
    if (e.key === "Escape") {
      escapeAction();
      return;
    }
    if (e.key === "Enter" && mode === "flag") {
      if (flagChain) finishChain(false);
      else if (flagSel.size) openSelectionDialog();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
      e.preventDefault();
      if (readOnly) return;
      e.shiftKey ? doRedo() : doUndo();
      return;
    }
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "y") {
      e.preventDefault();
      if (!readOnly) doRedo();
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (e.key >= "1" && e.key <= "9") {
      const cid = barClasses[Number(e.key) - 1];
      if (cid !== undefined && !readOnly) selectClass(cid);
      return;
    }
    const m = ALL_MODES.find((mm) => modeKeys[mm] === e.key.toLowerCase());
    if (m && (!readOnly || m === "measure" || m === "flag")) {
      // measure and flag-viewing also work on read-only tiles
      setMode(m);
      return;
    }
    if (e.key.toLowerCase() === "h" || e.key === "?") {
      showHelp(helpKeys());
      return;
    }
    if (e.key.toLowerCase() === "g") {
      toggleGrid();
      return;
    }
    if (e.key.toLowerCase() === "v") {
      toggleLabels();
      return;
    }
    if (e.key.toLowerCase() === "f") {
      viewport.fit(payload.ref.width, payload.ref.height);
      requestRender();
    }
    if (e.key === "+" || e.key === "=") { viewport.zoomAt(canvas.clientWidth / 2, canvas.clientHeight / 2, 1.25); requestRender(); }
    if (e.key === "-") { viewport.zoomAt(canvas.clientWidth / 2, canvas.clientHeight / 2, 0.8); requestRender(); }
  }

  function onKeyUp(e: KeyboardEvent): void {
    if (e.key === " ") spaceHeld = false;
  }

  function onResize(): void {
    viewport.resize();
    requestRender();
  }

  function onVisibility(): void {
    if (document.hidden && graph.rev !== savedRev) {
      localBackup();
      void save();
    }
    if (document.hidden && gridDirty) pushGrid();
  }

  function onBeforeUnload(e: BeforeUnloadEvent): void {
    if (graph.rev !== savedRev) {
      localBackup();
      e.preventDefault();
    }
  }

  canvas.addEventListener("pointerdown", onPointerDown);
  canvas.addEventListener("pointermove", onPointerMove);
  canvas.addEventListener("pointerup", onPointerUp);
  canvas.addEventListener("pointercancel", onPointerUp);
  canvas.addEventListener("wheel", onWheel, { passive: false });
  canvas.addEventListener("contextmenu", (e) => e.preventDefault());
  window.addEventListener("keydown", onKeyDown);
  window.addEventListener("keyup", onKeyUp);
  window.addEventListener("resize", onResize);
  document.addEventListener("visibilitychange", onVisibility);
  window.addEventListener("beforeunload", onBeforeUnload);

  // ---------- timers ----------
  const timers: number[] = [];
  if (!readOnly) {
    timers.push(window.setInterval(() => void save(), 25_000)); // autosave
    timers.push(window.setInterval(() => {
      if (graph.rev !== savedRev) localBackup();
    }, 5_000));
    if (!retouche) {
      // retouche is untimed: no heartbeats, the owner's time chip stays frozen
      timers.push(window.setInterval(async () => {
        // heartbeat: only counts when the user is actually here and active
        if (document.hidden || Date.now() - lastActivity > inactivityMs) return;
        try {
          const r = await api<{ seconds: number; admin_flags: AdminFlag[]; flag_replies: Record<string, FlagReply[]>;
                               removed_flags?: number[] }>(
            `/api/assignments/${assignmentId}/heartbeat`, { method: "POST" });
          seconds = r.seconds;
          updateTimeChip();
          // flags an admin removed while this tile is open (the server strips them from saves anyway)
          const gone = (r.removed_flags ?? []).filter((i) => graph.flags.has(i));
          if (gone.length) {
            for (const i of gone) dropFlag(i);
            toast(gone.length === 1 ? "An admin removed one of your flags" : `An admin removed ${gone.length} of your flags`);
            updateFlagChip();
            requestRender();
          }
          // the heartbeat carries the flag state along, so admin flags and
          // replies planted while the labeler works show up without a reload
          const seen = adminFlags.size + Object.values(flagReplies).reduce((n, l) => n + l.length, 0);
          const got = r.admin_flags.length + Object.values(r.flag_replies).reduce((n, l) => n + l.length, 0);
          if (seen !== got) {
            adminFlags.clear();
            for (const f of r.admin_flags) adminFlags.set(f.i, f);
            for (const k of Object.keys(flagReplies)) delete flagReplies[k];
            Object.assign(flagReplies, r.flag_replies);
            updateFlagChip();
            requestRender();
          }
        } catch { /* offline: ignore */ }
      }, 20_000));
      timers.push(window.setInterval(() => {
        if (!document.hidden && Date.now() - lastActivity <= inactivityMs) {
          seconds += 1;
          updateTimeChip();
        }
      }, 1_000));
    }
  }

  // ---------- boot ----------
  viewport.resize();
  viewport.fit(payload.ref.width, payload.ref.height);
  updateHud();
  requestRender();

  return () => {
    disposed = true;
    cancelRebind?.();
    view3dOpen = false;
    view3d?.dispose();
    for (const t of timers) clearInterval(t);
    canvas.removeEventListener("wheel", onWheel);
    window.removeEventListener("keydown", onKeyDown);
    window.removeEventListener("keyup", onKeyUp);
    window.removeEventListener("resize", onResize);
    document.removeEventListener("visibilitychange", onVisibility);
    window.removeEventListener("beforeunload", onBeforeUnload);
    if (!readOnly && graph.rev !== savedRev) {
      pruneOutOfBounds();
      localBackup();
      // best-effort final save while navigating away
      fetch(`/api/assignments/${assignmentId}/graph`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ graph: graph.serialize() }),
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => undefined);
    }
    if (!readOnly && gridDirty) {
      window.clearTimeout(gridSyncTimer);
      fetch(`/api/assignments/${assignmentId}/grid`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nx: gridState.nx, ny: gridState.ny, checked: [...gridChecked] }),
        keepalive: true,
        credentials: "same-origin",
      }).catch(() => undefined);
    }
  };
}
